/* Maquettine studio flow.
   Photos -> pet -> sketch -> size -> details -> Shopify checkout.

   Every step is saved to a draft on the figurine service (data-api). Photos are
   downscaled in the browser, uploaded one by one, and the draft token is put in
   the cart line as the hidden `_studio` property. The paid-order webhook claims
   the draft, so the order arrives complete. Ordering is blocked until every
   photo has arrived: an order without its photos cannot be fulfilled.

   Progress survives a return visit: the token, the typed details and the step live in
   localStorage, and the photos and sketch are fetched back from the draft. Until the
   draft is ordered, every studio button on the site reads "Continue with {name}".

   Leaving the photo step asks the service for an AI sketch, which renders while the
   customer types the pet's details and is shown on the step after. The sketch is a
   mood picture, never the figurine: the markup around it says so in every state. */

/* Pure studio logic, shared with the node tests in tests/js. No DOM in here. */
var MqStudioLogic = (function () {
  'use strict';

  /* One figure per pet. From two pets on, the whole order gets `percent` off: one flat
     discount, not per pet, so five pets are 20% off too, whatever sizes they mix. Mirrors
     the automatic "amount off order" discount in Shopify (minimum two items). Prices are
     whole euros, so 20% is exact to the cent on every line. `units` is one price per pet. */
  function pricingMixed(units, percent) {
    units = (units || []).slice(0, 5);
    percent = typeof percent === 'number' ? percent : 20;
    var lines = [];
    var subtotal = 0;
    var discount = 0;
    units.forEach(function (unit, i) {
      var off = units.length < 2 ? 0 : Math.round(unit * percent / 100);
      subtotal += unit;
      discount += off;
      lines.push({ position: i + 1, full: unit, discount: off, cents: unit - off });
    });
    return { lines: lines, subtotal: subtotal, discount: discount, total: subtotal - discount };
  }

  /* The deposit split: `units` the figurine prices, `deposits` the deposit variant prices,
     one each per pet. The multi-pet discount is on the deposit lines too (the Shopify
     discount covers both products), so today and later are both 20% off from two pets.
     Deposits are multiples of 5 euro, so 80% of one is always whole euros.
     `lines[i].later` is what that pet adds at approval: the same number the backend puts
     on the order. */
  function depositSplit(units, deposits, percent) {
    var full = pricingMixed(units, percent);
    var now = pricingMixed(deposits, percent);
    var lines = full.lines.map(function (line, i) {
      var paid = now.lines[i] ? now.lines[i].cents : 0;
      return { position: line.position, total: line.cents, now: paid, later: line.cents - paid };
    });
    return { lines: lines, subtotal: full.subtotal, discount: full.discount, total: full.total,
             now: now.total, later: full.total - now.total };
  }

  /* All pets the same size. */
  function pricing(unitCents, count, percent) {
    count = Math.max(1, Math.min(5, count | 0));
    var units = [];
    for (var i = 0; i < count; i++) units.push(unitCents);
    return pricingMixed(units, percent);
  }

  /* Why "Verder" does not go on with several pets, or '' when it can.
     pets: [{ position, name, ok, sending, failed }]. `part` is 'photos' for the first step
     (names and photos) and 'details' for the pet step, where only the name is required:
     the kind of animal is read from the photos by the service.
     The first problem wins, in card order, so the message points at the pet to fix. */
  function petsProblem(pets, minPhotos, text, part) {
    for (var i = 0; i < pets.length; i++) {
      var pet = pets[i];
      var label = pet.name || text.petN.replace('{n}', pet.position);
      if (!pet.name) return text.name.replace('{n}', pet.position);
      if (part !== 'details') {
        if (pet.failed) return text.failed;
        if (pet.sending) return text.uploading.replace('{name}', label);
        if (pet.ok < minPhotos) return text.photos.replace('{name}', label);
      }
    }
    return '';
  }

  /* "Benji en Tessa", "Bo & Jo", "Max, Luna and Rex" -> one name per pet, at most five.
     Connectors are whole words in any of the four languages; "Benji" or "Rene" stay whole. */
  function splitNames(text) {
    var out = [];
    var current = [];
    String(text || '').replace(/[,&+\/]/g, ' , ').split(/\s+/).forEach(function (word) {
      if (!word) return;
      if (word === ',' || /^(en|and|et|und)$/i.test(word)) {
        if (current.length) out.push(current.join(' '));
        current = [];
      } else {
        current.push(word);
      }
    });
    if (current.length) out.push(current.join(' '));
    return out.slice(0, 5);
  }

  function joinNames(names, and) {
    names = names.filter(Boolean);
    if (names.length <= 1) return names[0] || '';
    return names.slice(0, -1).join(', ') + ' ' + and + ' ' + names[names.length - 1];
  }

  /* What the cart holds, against what the studio wants. Variants first: a swapped size is
     another variant, and only an add can put one in the cart, which is the one thing Shopify
     counts as an addition. Details typed earlier — a name, a note — live in the properties
     and are edited where they stand, so they never count again. */
  /* Shopify does not hand the lines back in the order they went in, so a line is paired
     with what the studio wants by the pet it belongs to, never by its position. Pairing by
     position read Molly's line as Dolly's, found every field changed, and then asked
     change.js to write one pet's details onto the other pet's line: with two pets the same
     size that makes two identical lines, which Shopify refuses with a 422. */
  function cartKey(props) {
    props = props || {};
    return String(props._pet == null || props._pet === '' ? '1' : props._pet);
  }

  /* Per intended item, the 1-based line in the cart that holds that pet, or 0 for none. */
  function cartPairs(lines, items) {
    var byKey = {};
    (lines || []).forEach(function (line, i) {
      var key = cartKey(line.properties);
      if (!(key in byKey)) byKey[key] = i + 1;
    });
    return (items || []).map(function (item) { return byKey[cartKey(item.properties)] || 0; });
  }

  function cartVariantsMatch(lines, items) {
    if (!items || !items.length || !lines || lines.length !== items.length) return false;
    var pairs = cartPairs(lines, items);
    var seen = {};
    return items.every(function (item, i) {
      var at = pairs[i];
      /* No line for this pet, or two pets landing on one line: not a match. */
      if (!at || seen[at]) return false;
      seen[at] = true;
      var line = lines[at - 1];
      return Number(line.variant_id || line.id) === Number(item.id) &&
        Number(line.quantity) === Number(item.quantity || 1);
    });
  }

  /* Shopify hands properties back as strings, and drops the ones it was given empty, so
     a key it never stored is not a change. Missing and empty count as the same thing, on
     both sides: otherwise the order button would find drift that cannot be written away
     and would edit the line on every single click. */
  function cartPropsMatch(line, item) {
    var have = line.properties || {};
    var want = item.properties || {};
    var keys = {};
    Object.keys(have).forEach(function (key) { keys[key] = true; });
    Object.keys(want).forEach(function (key) { keys[key] = true; });
    return Object.keys(keys).every(function (key) {
      return String(have[key] == null ? '' : have[key]) === String(want[key] == null ? '' : want[key]);
    });
  }

  /* The lines whose properties drifted, as {line, item}: the 1-based line in the cart and
     which intended item belongs on it. Empty means the cart is already right. Only called
     once cartVariantsMatch has paired every item, so every pair is there. */
  function cartDrift(lines, items) {
    var pairs = cartPairs(lines, items);
    var out = [];
    (items || []).forEach(function (item, i) {
      var at = pairs[i];
      if (at && !cartPropsMatch(lines[at - 1], item)) out.push({ line: at, item: i });
    });
    return out;
  }

  return { pricing: pricing, pricingMixed: pricingMixed, depositSplit: depositSplit, petsProblem: petsProblem, joinNames: joinNames, splitNames: splitNames,
           cartVariantsMatch: cartVariantsMatch, cartPropsMatch: cartPropsMatch, cartDrift: cartDrift,
           cartPairs: cartPairs };
})();

if (typeof module !== 'undefined' && module.exports) module.exports = MqStudioLogic;

(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') return;

  var STEPS = ['photos', 'pet', 'sketch', 'size', 'details'];
  /* Their figurine next to the mug: how long the size step shows a loader for it, and how
     often it asks, before the stock figurine stands in. */
  var FIGURE_TRIES = 10;
  var STORE_KEY = 'mq.studio.v2';
  var MAX_EDGE = 2048;
  var MAX_PHOTOS = 8;
  var SKETCH_POLL_MS = 2500;
  /* The look at the photos waits this long after the last one landed, so one call covers a burst. */
  var CHECK_DELAY_MS = 1500;
  /* The service gives up after three minutes; the page stops asking a little earlier. */
  var SKETCH_GIVE_UP_MS = 150000;
  var WAIT_LINE_MS = 2800;
  var MAX_PETS = 5;
  var Logic = MqStudioLogic;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function emptySketch() {
    return { status: 'none', url: '', attemptsLeft: 1, since: 0, timer: null };
  }

  /* A dialog opens with nothing focused but itself: the browser would otherwise land on
     the first button and draw a focus ring around it, as if it were chosen already. */
  function openDialog(dialog) {
    dialog.showModal();
    var inner = dialog.querySelector('.mq-why__inner');
    if (!inner) return;
    inner.tabIndex = -1;
    try { inner.focus({ preventScroll: true }); } catch (e) { inner.focus(); }
  }

  /* What the service saw on the photos: none | pending | ready. `level` is pass, mixed,
     two or block; `photos` maps a photo id to ok | none | unclear; `animals` carry a crop
     each when there are two to choose from. `fp` is the photo set it was made for. */
  function emptyCheck() {
    return { status: 'none', fp: '', level: '', seen: 0, photos: {}, animals: [], main: '', subject: '', praise: '', timer: null };
  }

  /* Pets 2 to 5. Pet 1 is the studio's own state, exactly as with a single pet. */
  function extraPet(position) {
    return { position: position, name: '', species: '', speciesOther: '', breed: '', note: '', size: '', photos: [], sketch: emptySketch(), check: emptyCheck() };
  }

  /* ---------------- first-party funnel ----------------
     Every studio step goes to the figurine service for the Retargeting page: a random
     visitor id, the step, the traffic source. No e-mail, no name. Batched, and sent
     with sendBeacon so the checkout click still arrives while the page unloads.
     Without analytics consent nothing is stored for it: the id is this page view's
     (snippets/mq-visit.liquid), so every studio visit still counts, as on OurBricks. */

  var EVENTS_URL = 'https://app.maquettine.com/api/public/events';
  var queue = [];
  var flushTimer = null;

  function randomId() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, ''); } catch (e) { /* old browser */ }
    return (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 32);
  }

  /* `kind` is 'local' or 'session': the store is looked up inside the guard, because with
     cookies blocked even reading window.localStorage throws. */
  function storedId(kind, key) {
    try {
      var store = kind === 'local' ? window.localStorage : window.sessionStorage;
      var id = store.getItem(key);
      if (!id) { id = randomId(); store.setItem(key, id); }
      return id;
    } catch (e) { return ''; }
  }

  /* Our own test browsers: open any page with ?pb=0 once and everything this browser
     sends is marked as a test, so Insights leaves it out. ?pb=1 undoes it. */
  function isTestBrowser() {
    try {
      var pb = new URLSearchParams(location.search).get('pb');
      if (pb === '0') localStorage.setItem('mq.test', '1');
      if (pb === '1') localStorage.removeItem('mq.test');
      return localStorage.getItem('mq.test') === '1';
    } catch (e) { return false; }
  }

  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed();
    } catch (e) { /* no consent API on this page */ }
    return true;
  }

  /* This page view's id, from the visit snippet: no storage, so no consent needed. */
  var pageId = (window.MaqVisit && window.MaqVisit.id) || randomId();

  function flush() {
    clearTimeout(flushTimer);
    flushTimer = null;
    if (!queue.length) return;
    var s = document.querySelector('mq-studio');
    var api = s && s.dataset.api ? s.dataset.api.replace(/\/public\/studio\/?$/, '/public/events') : EVENTS_URL;
    var stored = analyticsAllowed();
    var body = JSON.stringify({
      v: (stored && storedId('local', 'mq.vid')) || pageId,
      s: (stored && storedId('session', 'mq.sid')) || pageId,
      locale: (s && s.dataset.locale) || document.documentElement.lang || '',
      context: (s && s.dataset.context) || '',
      token: (s && s.state && s.state.token) || '',
      path: location.pathname,
      utm: utm(),
      test: isTestBrowser() ? 1 : 0,
      events: queue.splice(0, 20)
    });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(api, new Blob([body], { type: 'text/plain' }))) return;
    } catch (e) { /* fall through */ }
    try { fetch(api, { method: 'POST', body: body, keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } }); } catch (e) { /* analytics never breaks the flow */ }
  }

  function beacon(name, extra, now) {
    queue.push(Object.assign({ e: name }, extra || {}));
    if (now) { flush(); return; }
    if (!flushTimer) flushTimer = setTimeout(flush, 1500);
  }

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });

  /* A pixel event marks a milestone once, not an action every time it happens: Meta counts
     events, and a person going back and forth is still one person. So every event goes out
     once per draft (remembered in this browser, so a return to the same draft is not new),
     and before there is a draft, once per browser session. A block seen on the page counts
     once per block. */
  var TRACKED_KEY = 'mq.tracked';
  function alreadyTracked(event, payload) {
    var key = event;
    try {
      var studio = document.querySelector('mq-studio');
      var token = studio && studio.state ? (studio.state.token || '') : '';
      key = event + ':' + (payload && payload.block ? payload.block : '') + ':' + token;
      /* Even reading window.localStorage throws with cookies blocked: all of it is guarded. */
      var store = token ? window.localStorage : window.sessionStorage;
      var sent = JSON.parse(store.getItem(TRACKED_KEY) || '[]');
      if (sent.indexOf(key) !== -1) return true;
      store.setItem(TRACKED_KEY, JSON.stringify(sent.concat([key]).slice(-400)));
    } catch (e) { /* no storage: at worst one extra event, never a broken step */ }
    return { key: key };
  }

  function track(event, payload) {
    /* Nothing in here may ever stop a step: analytics is not the product. */
    var once;
    try {
      /* Our own test browsers (?pb=0) leave no trace in Meta either. */
      if (isTestBrowser()) return;
      once = alreadyTracked(event, payload);
    } catch (e) { once = { key: event }; }
    if (once === true) return;
    try {
      /* Lead is a standard Meta event, so ads can optimise for it. The studio steps are custom.
         The eventID lets Meta drop the same milestone if it ever hears it twice. */
      if (typeof window.fbq === 'function') window.fbq(event === 'Lead' ? 'track' : 'trackCustom', event, payload || {}, { eventID: once.key });
    } catch (e) { /* analytics must never break the flow */ }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'mq_' + event }, payload || {}));
    } catch (e) { /* same */ }
    document.dispatchEvent(new CustomEvent('mq:' + event, { detail: payload || {} }));
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-BE', {
        style: 'currency', currency: currency || 'EUR',
        minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: cents % 100 ? 2 : 0
      }).format(cents / 100);
    } catch (e) {
      return '€' + Math.round(cents / 100);
    }
  }

  function utm() {
    var out = {};
    try {
      var params = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = params.get(k);
        if (v) out[k] = v;
      });
      /* Ads without UTM tags still carry a click id: Meta adds fbclid, Google adds gclid.
         Name the source from it, so an untagged ad does not show up as "direct". */
      if (!out.utm_source && params.get('fbclid')) { out.utm_source = 'meta'; out.utm_medium = out.utm_medium || 'paid'; }
      if (!out.utm_source && params.get('gclid')) { out.utm_source = 'google'; out.utm_medium = out.utm_medium || 'cpc'; }
      var saved = JSON.parse(sessionStorage.getItem('mq.utm') || '{}');
      out = Object.assign({}, saved, out);
      sessionStorage.setItem('mq.utm', JSON.stringify(out));
    } catch (e) { /* private mode */ }
    return out;
  }

  function isHeic(file) {
    return /hei[cf]/i.test(file.type + ' ' + file.name);
  }

  /* Downscale to MAX_EDGE on the long side and re-encode as JPEG. Re-encoding drops
     EXIF; the browser applies orientation while decoding, so the result is upright.
     HEIC is always re-encoded when the browser can decode it (Safari can), so it
     arrives as JPEG. Anything the browser cannot decode (HEIC in Chrome, Firefox and
     on Windows) is sent as-is and converted by the service. */
  function shrink(file) {
    var heic = isHeic(file);
    if (!heic && !/^image\//.test(file.type)) {
      return Promise.resolve(file);
    }
    var bitmap = window.createImageBitmap
      ? createImageBitmap(file, { imageOrientation: 'from-image' })
      : Promise.reject(new Error('no bitmap'));
    return bitmap.then(function (img) {
      var scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      if (!heic && scale === 1 && file.size < 4 * 1024 * 1024) { img.close && img.close(); return file; }
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      img.close && img.close();
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          if (!blob) return resolve(file);
          var name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.9);
      });
    }).catch(function () { return file; });
  }

  class MqStudio extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.minPhotos = parseInt(this.dataset.minPhotos || '3', 10);
      this.state = {
        token: '', petName: '', species: '', speciesOther: '', breed: '', note: '',
        email: '', phone: '', size: '',
        /* Several pets: 'same' size for all, or 'each' pet its own. Pet 1 always uses `size`. */
        sizeMode: 'same',
        stance: this.dataset.stance || 'sitting'
      };
      this.photos = []; /* { id, name, url, state: sending|ok|fail, file } */
      /* status: none | pending | ready | failed. url is absolute once known. */
      this.sketch = emptySketch();
      this.check = emptyCheck();
      /* Several pets in one order: one figure each, same size. With one pet nothing below
         is on screen and the flow is exactly the single-pet flow. */
      this.numPets = 1;
      this.extra = [extraPet(2), extraPet(3), extraPet(4), extraPet(5)];
      this.countFor = 0;
      this.stepIndex = 0;
      this.saving = null;

      this.bar = this.querySelector('[data-mq-progress]');
      this.steps = Array.from(this.querySelectorAll('[data-mq-step]'));
      this.errorBox = this.querySelector('[data-mq-error]');

      this.sizes = {};
      Array.from(this.querySelectorAll('[data-mq-size-option]')).forEach(function (el) {
        this.sizes[el.dataset.mqSizeOption] = {
          label: el.dataset.label || '',
          price: parseInt(el.dataset.price || '0', 10),
          variant: el.dataset.variant || '',
          /* Deposit checkout: this variant goes in the cart instead, at `deposit` cents. */
          deposit: parseInt(el.dataset.deposit || '0', 10),
          depositVariant: el.dataset.depositVariant || ''
        };
        var radio = el.querySelector('input');
        if (radio && radio.checked) { this.state.size = radio.value; this.sizeIsDefault = true; this.shopSize = radio.value; }
      }, this);

      this.querySelectorAll('[data-mq-next]').forEach(function (btn) {
        btn.addEventListener('click', this.next.bind(this));
      }, this);
      this.querySelectorAll('[data-mq-back]').forEach(function (btn) {
        btn.addEventListener('click', this.back.bind(this));
      }, this);
      this.querySelectorAll('[data-mq-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }, this);
      this.addEventListener('input', this.onChange.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      var addBtn = this.querySelector('[data-mq-add]');
      if (addBtn) addBtn.addEventListener('click', this.addToCart.bind(this));

      var more = this.querySelector('[data-mq-add-photos]');
      if (more) more.addEventListener('change', function () {
        this.addFiles(Array.from(more.files || []));
        more.value = '';
      }.bind(this));

      /* Price explainer on the size step. A tap on the backdrop closes it too. */
      var why = this.querySelector('[data-mq-why]');
      var whyOpen = this.querySelector('[data-mq-why-open]');
      if (why && whyOpen && typeof why.showModal === 'function') {
        whyOpen.addEventListener('click', function () {
          openDialog(why);
          track('PriceExplainer', { size: this.state.size });
          beacon('price_explainer', { props: { size: this.state.size } });
        }.bind(this));
        why.querySelectorAll('[data-mq-why-close]').forEach(function (btn) {
          btn.addEventListener('click', function () { why.close(); });
        });
        why.addEventListener('click', function (e) { if (e.target === why) why.close(); });
      } else if (whyOpen) {
        whyOpen.closest('p').hidden = true;
      }

      var restart = this.querySelector('[data-mq-restart]');
      if (restart) restart.addEventListener('click', this.startOver.bind(this));

      var list = this.querySelector('[data-mq-photos]');
      if (list) list.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-mq-remove]');
        if (btn) this.removePhoto(btn.dataset.mqRemove);
      }.bind(this));

      this.bindPets();
      this.adoptResumeLink();
      this.restore();
      this.buildPets();
      this.syncNameField();
      this.renderPhotos();
      this.renderSketch();
      this.render();
      this.resume();
    }

    /* ---------------- several pets ---------------- */

    multi() {
      return this.numPets > 1;
    }

    /* A pet as one shape, whichever pet it is. Pet 1 reads and writes the studio state. */
    pet(position) {
      var self = this;
      if (position !== 1) return this.extra[position - 2];
      return {
        position: 1,
        get name() { return self.state.petName; }, set name(v) { self.state.petName = v; },
        get species() { return self.state.species; }, set species(v) { self.state.species = v; },
        get speciesOther() { return self.state.speciesOther; }, set speciesOther(v) { self.state.speciesOther = v; },
        get size() { return self.state.size; }, set size(v) { self.state.size = v; },
        get breed() { return self.state.breed; }, set breed(v) { self.state.breed = v; },
        get note() { return self.state.note; }, set note(v) { self.state.note = v; },
        get photos() { return self.photos; }, set photos(v) { self.photos = v; },
        get sketch() { return self.sketch; }, set sketch(v) { self.sketch = v; },
        get check() { return self.check; }, set check(v) { self.check = v; }
      };
    }

    petList() {
      var out = [];
      for (var i = 1; i <= this.numPets; i++) out.push(this.pet(i));
      return out;
    }

    petLabel(pet) {
      return pet.name || (this.dataset.txtPetN || 'Pet {n}').replace('{n}', pet.position);
    }

    /* The name(s) in sentences: "Tessa", or "Tessa en Moos" with several pets. */
    displayName() {
      if (!this.multi()) return this.state.petName;
      return Logic.joinNames(this.petList().map(function (p) { return p.name; }), this.dataset.txtAnd || '&');
    }

    bindPets() {
      var self = this;
      var dialog = this.querySelector('[data-mq-pets-dialog]');
      var confirmText = dialog && dialog.querySelector('[data-mq-pets-confirm-text]');
      /* One more pet: a new card, straight into view. Only the last card can be removed,
         and removing one with photos asks first. */
      this.querySelectorAll('[data-mq-pet-add]').forEach(function (add) {
        add.addEventListener('click', function () { self.addPet(); });
      });
      /* Size step with several pets: one size for all, or a size per pet. */
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-mq-size-mode]')) {
          self.setSizeMode(self.state.sizeMode === 'each' ? 'same' : 'each');
          return;
        }
        var opt = e.target.closest('[data-mq-pet-size]');
        if (opt) self.setPetSize(Number(opt.closest('[data-mq-size-pet]').dataset.mqSizePet), opt.dataset.mqPetSize);
        var dot = e.target.closest('[data-mq-sketch-dot]');
        if (dot) {
          var track = self.querySelector('[data-mq-sketches]');
          if (track) track.scrollTo({ left: Number(dot.dataset.mqSketchDot) * track.clientWidth, behavior: 'smooth' });
        }
      });
      var track = this.querySelector('[data-mq-sketches]');
      if (track) track.addEventListener('scroll', function () { self.syncSketchDots(); }, { passive: true });
      var undo = this.querySelector('[data-mq-set-undo]');
      if (undo) undo.addEventListener('click', function () {
        /* "Bo en Jo" is one pet after all. */
        self.askToShrink(1, function () {
          self.state.unsplit = true;
          self.applyNames();
          self.render();
        });
      });
      var nameInput = this.querySelector('[data-mq-first-name]');
      if (nameInput) nameInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); nameInput.blur(); }
      });
      this.addEventListener('click', function (e) {
        var remove = e.target.closest('[data-mq-pet-remove]');
        if (!remove) return;
        self.askToRemove(Number(remove.dataset.mqPetRemove));
      });
      if (dialog) {
        dialog.querySelectorAll('[data-mq-pets-close]').forEach(function (btn) {
          btn.addEventListener('click', function () { dialog.close(); });
        });
        dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
        var ok = dialog.querySelector('[data-mq-pets-confirm-ok]');
        if (ok) ok.addEventListener('click', function () {
          var count = self.pendingCount;
          var after = self.pendingAfter;
          dialog.close();
          if (count) self.setPetCount(count);
          if (after) after();
        });
        dialog.addEventListener('close', function () { self.pendingCount = 0; self.pendingAfter = null; });
      }

      var redoDialog = this.querySelector('[data-mq-redo-dialog]');
      if (redoDialog) {
        redoDialog.addEventListener('click', function (e) { if (e.target === redoDialog) redoDialog.close(); });
        redoDialog.querySelectorAll('[data-mq-redo-close]').forEach(function (btn) {
          btn.addEventListener('click', function () { redoDialog.close(); });
        });
        var redoOk = redoDialog.querySelector('[data-mq-redo-ok]');
        if (redoOk) redoOk.addEventListener('click', function () {
          var position = self.pendingRedo || 1;
          redoDialog.close();
          self.redoPhotos(position);
        });
        redoDialog.addEventListener('close', function () { self.pendingRedo = 0; });
      }

      var checkDialog = this.querySelector('[data-mq-check-dialog]');
      if (checkDialog) {
        checkDialog.addEventListener('click', function (e) { if (e.target === checkDialog) checkDialog.close(); });
        var more = checkDialog.querySelector('[data-mq-check-more]');
        if (more) more.addEventListener('click', function () {
          checkDialog.close();
          self.goToPhotos(self.noSketchPet || 1);
        });
        var go = checkDialog.querySelector('[data-mq-check-go]');
        if (go) go.addEventListener('click', function () {
          var after = self.pendingNoSketch;
          checkDialog.close();
          if (after) after();
        });
        checkDialog.addEventListener('close', function () { self.pendingNoSketch = null; });
      }

      var restartDialog = this.querySelector('[data-mq-restart-dialog]');
      if (restartDialog) {
        restartDialog.querySelectorAll('[data-mq-restart-close]').forEach(function (btn) {
          btn.addEventListener('click', function () { restartDialog.close(); });
        });
        restartDialog.addEventListener('click', function (e) { if (e.target === restartDialog) restartDialog.close(); });
        restartDialog.querySelectorAll('[data-mq-restart-ok]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var after = self.pendingRestart;
            restartDialog.close();
            if (after) after();
          });
        });
        var restartAdd = restartDialog.querySelector('[data-mq-restart-add]');
        if (restartAdd) restartAdd.addEventListener('click', function () {
          restartDialog.close();
          self.addPet('restart');
        });
        restartDialog.addEventListener('close', function () { self.pendingRestart = null; });
      }

      /* The cards and the per-pet fields are built by the script; listen once, on the studio. */
      this.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-mq-slot], [data-mq-card-slot]')) {
          e.preventDefault();
          e.target.click();
        }
      });
      this.addEventListener('click', function (e) {
        var redo = e.target.closest('.mq-sketch__redo');
        if (redo) { self.askRedo(self.framePet(redo)); return; }
        var addOnSketch = e.target.closest('.mq-sketch__addpet');
        if (addOnSketch) { self.addPet('sketch'); return; }
        var more = e.target.closest('.mq-sketch__more');
        if (more) { var fig = more.closest('[data-mq-pet]'); self.goToPhotos(fig ? Number(fig.dataset.mqPet) : 1); return; }
        var pick = e.target.closest('[data-mq-check-pick]');
        if (pick) { self.pickSubject(self.checkPosition(pick), pick.dataset.mqCheckPick); return; }
        if (e.target.closest('[data-mq-check-both]')) { self.splitPet(self.checkPosition(e.target)); return; }
        var remove = e.target.closest('[data-mq-pet] [data-mq-remove]');
        if (remove) {
          self.removePhoto(remove.dataset.mqRemove, Number(remove.closest('[data-mq-pet]').dataset.mqPet));
          return;
        }
        var slot = e.target.closest('[data-mq-pet] [data-mq-card-slot]');
        if (slot) {
          var input = slot.closest('[data-mq-pet]').querySelector('[data-mq-card-add]');
          if (input) input.click();
        }
      });
      this.addEventListener('change', function (e) {
        var input = e.target.closest('[data-mq-card-add]');
        if (!input) return;
        self.addFiles(Array.from(input.files || []), Number(input.closest('[data-mq-pet]').dataset.mqPet));
        input.value = '';
      });
    }

    /* One more pet: back to the photos, where the names are, with a new card and the cursor
       after "Benji en ". The pets already there keep their photos and sketches; only the
       new one gets a sketch when the photo step is left again. `via` says which button. */
    addPet(via) {
      if (this.numPets >= MAX_PETS) return;
      var step = STEPS[this.stepIndex];
      this.petVia = via || step;
      if (step !== 'photos') {
        this.stepIndex = 0;
        this.showError('');
        this.render();
      }
      /* The new pet's name goes where the other names are: "Benji" becomes "Benji en ". */
      var text = (this.state.nameText || '').trim();
      this.state.unsplit = false;
      if (text) this.setNameText(text + ' ' + (this.dataset.txtAnd || '&') + ' ');
      this.setPetCount(this.numPets + 1);
      this.petVia = '';
      this.nameCount = this.numPets;
      var input = this.querySelector('[data-mq-first-name]');
      if (input) {
        input.focus();
        try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) { /* not a text input */ }
      }
    }

    /* One pet out, any of them. Its photos are asked about first, like everywhere else;
       while photos are still uploading nothing moves, since an upload is addressed by
       the pet's place. */
    askToRemove(position) {
      var self = this;
      if (this.numPets < 2 || position < 1 || position > this.numPets) return;
      var sending = this.petList().filter(function (pet) {
        return pet.photos.some(function (p) { return p.state === 'sending'; });
      })[0];
      if (sending) { this.showError((this.dataset.txtUploading || '').replace('{name}', this.petLabel(sending))); return; }
      var pet = this.pet(position);
      var dialog = this.querySelector('[data-mq-pets-dialog]');
      if (pet.photos.length && dialog && typeof dialog.showModal === 'function') {
        this.pendingCount = 0;
        this.pendingAfter = function () { self.removePet(position); };
        dialog.querySelector('[data-mq-pets-confirm-text]').textContent = (this.dataset.txtMultiRemove || '').replace('{names}', this.petLabel(pet));
        openDialog(dialog);
        return;
      }
      this.removePet(position);
    }

    /* The draft drops the pet first and moves the others up; then this page does the same.
       If the draft says no, nothing changes here either. */
    removePet(position) {
      var self = this;
      if (!this.state.token) { this.dropPet(position); return; }
      this.syncCount().then(function (token) {
        return fetch(self.api + '/drafts/' + encodeURIComponent(token) + '/pets/' + position, {
          method: 'DELETE', mode: 'cors', credentials: 'omit'
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('remove ' + r.status);
        self.countFor = self.numPets - 1;
        self.countPromise = Promise.resolve(self.state.token);
        self.dropPet(position);
      }).catch(function () {
        self.showError(self.dataset.errCart || '');
      });
    }

    /* Every pet after `position` moves up a place with its name, details, photos and
       sketch. Down to one pet this is the single-pet studio again, with that pet in it. */
    dropPet(position) {
      var self = this;
      var n = this.numPets;
      var keys = ['name', 'species', 'speciesOther', 'breed', 'note', 'size', 'photos', 'sketch', 'check'];
      var list = this.petList().map(function (pet) {
        var data = {};
        keys.forEach(function (k) { data[k] = pet[k]; });
        clearTimeout(data.sketch.timer);
        clearTimeout(data.sketch.figureTimer);
        clearTimeout(data.check.timer);
        data.check.timer = null;
        data.sketch.figureTimer = null;
        return data;
      });
      var gone = list.splice(position - 1, 1)[0];
      gone.photos.forEach(function (photo) { if (photo.url && photo.file) { try { URL.revokeObjectURL(photo.url); } catch (e) { /* ignore */ } } });
      /* Pet 1 always has the order's size; a pet moving into first place brings its own. */
      var orderSize = position === 1 && this.state.sizeMode === 'each' && list[0].size ? list[0].size : this.state.size;
      list.forEach(function (data, i) {
        var pet = self.pet(i + 1);
        keys.forEach(function (k) { if (k !== 'size' || i > 0) pet[k] = data[k]; });
      });
      this.state.size = orderSize;
      var radio = this.querySelector('[name="mq-size"][value="' + orderSize + '"]');
      if (radio) radio.checked = true;
      /* The last place is empty now; setPetCount clears it without touching the pets that moved. */
      this.extra[n - 2] = extraPet(n);
      if (position === 1) { this.figureShown = ''; this.figureTries = 0; }
      this.petVia = 'remove';
      this.setPetCount(n - 1);
      this.petVia = '';
      /* The names field says who is left, in order; unnamed pets keep their place. */
      this.state.unsplit = false;
      var names = list.map(function (data) { return data.name; });
      var text = Logic.joinNames(names, this.dataset.txtAnd || '&');
      /* So an unnamed pet, missing from the text, does not read as one pet fewer. */
      this.nameCount = Math.max(Logic.splitNames(text).length, 1);
      this.setNameText(text);
      this.petList().forEach(function (pet, i) { pet.name = names[i] || ''; });
      this.nameCount = this.numPets;
      if (this.numPets === 1) this.fillSingleInputs();
      /* Sketches still coming are asked for at their new place. */
      this.petList().forEach(function (pet) {
        if (pet.sketch.status === 'pending') self.pollSketch(pet.position);
      });
      this.showError('');
      this.syncPets();
      this.renderPhotos();
      this.renderSketch();
      this.applyFigure();
      this.render();
      this.persist();
    }

    /* No sketch from these photos: before they reach the sketch step, they hear it and
       choose. A look still under way (rare by now) is not waited for: the sketch step
       says it then. True means go ahead; false means the answer comes through `after`. */
    confirmNoSketch(after) {
      var dialog = this.querySelector('[data-mq-check-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return true;
      var pets = this.petList();
      var self = this;
      var blocked = pets.filter(function (p) {
        return self.checkCurrent(p) && p.check.level === 'block' && p.sketch.status !== 'ready' && p.check.accepted !== p.check.fp;
      });
      if (!blocked.length) return true;
      var names = Logic.joinNames(blocked.map(function (p) { return self.petLabel(p); }), this.dataset.txtAnd || '&');
      var text = dialog.querySelector('[data-mq-check-dialog-text]');
      if (text) text.textContent = (text.dataset.template || '').replace(/\{name\}/g, names);
      this.pendingNoSketch = function () {
        blocked.forEach(function (p) { p.check.accepted = p.check.fp; });
        beacon('photo_check', { props: { level: 'block', accepted: 1, pet: blocked[0].position } });
        after();
      };
      this.noSketchPet = blocked[0].position;
      openDialog(dialog);
      return false;
    }

    /* Back to the photo step, with the note that says what is missing in view. No picker
       yet: first they read which photos do not count. */
    goToPhotos(position) {
      var self = this;
      this.stepIndex = 0;
      this.showError('');
      this.render();
      this.syncCheck();
      requestAnimationFrame(function () {
        var target = self.multi()
          ? self.querySelector('[data-mq-pet-cards] [data-mq-pet="' + (position || 1) + '"]')
          : self.querySelector('[data-mq-step="photos"] [data-mq-single-only] [data-mq-check-note]');
        if (!target || target.hidden) return;
        try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { target.scrollIntoView(); }
      });
    }

    /* Which pet a note under the photos belongs to: the card's, or pet 1 with one pet. */
    checkPosition(el) {
      var card = el.closest('[data-mq-pet]');
      return card ? Number(card.dataset.mqPet) : 1;
    }

    /* Down to `count` pets, asking first when that throws photos away; then `after`. */
    askToShrink(count, after) {
      var self = this;
      var dialog = this.querySelector('[data-mq-pets-dialog]');
      var losing = this.extra.filter(function (p) { return p.position > count && p.position <= self.numPets && p.photos.length; });
      if (losing.length && dialog && typeof dialog.showModal === 'function') {
        this.pendingCount = count;
        this.pendingAfter = after;
        var names = Logic.joinNames(losing.map(function (p) { return self.petLabel(p); }), this.dataset.txtAnd || '&');
        dialog.querySelector('[data-mq-pets-confirm-text]').textContent = (this.dataset.txtMultiRemove || '').replace('{names}', names);
        openDialog(dialog);
        return;
      }
      this.setPetCount(count);
      if (after) after();
    }

    setNameText(text) {
      this.state.nameText = text;
      var input = this.querySelector('[data-mq-first-name]');
      if (input && input.value !== text) input.value = text;
      this.applyNames();
    }

    /* The name field is the one place names are typed. Two or more names there make two
       or more pets straight away; fewer names only remove cards that have no photos yet. */
    /* The restart icon shows as soon as there is something to throw away: a name or a photo,
       also on the first step, before any step change. */
    syncRestart() {
      var restart = this.querySelector('[data-mq-restart]');
      if (restart) restart.hidden = !(this.photos.length || this.state.petName || this.extra.some(function (p) { return p.photos.length; }));
    }

    applyNames() {
      var text = this.state.nameText || '';
      var names = this.state.unsplit ? [text.trim()].filter(Boolean) : Logic.splitNames(text);
      if (!names.length) this.state.unsplit = false;
      var before = this.nameCount || 1;
      if (!this.state.unsplit && names.length > this.numPets) {
        this.setPetCount(names.length);
      } else if (names.length < before && this.numPets > Math.max(names.length, 1)) {
        var keep = this.numPets;
        while (keep > Math.max(names.length, 1) && !this.pet(keep).photos.length) keep -= 1;
        if (keep < this.numPets) this.setPetCount(keep);
      }
      this.nameCount = Math.max(names.length, 1);
      this.petList().forEach(function (pet, i) { pet.name = names[i] || ''; });
      this.syncRestart();
      if (this.revealed !== true && (text.trim() || this.hasPhotos())) this.revealed = true;
      var reveal = this.querySelector('[data-mq-reveal]');
      if (reveal) reveal.hidden = !this.revealed;
      this.syncPets();
    }

    /* The name field and the photos under it, after a restore or a resume. Drafts from
       before names came first have no typed text: their pet names make one. */
    syncNameField() {
      if (!this.state.nameText) this.state.nameText = this.displayName() || '';
      var text = this.state.nameText;
      var input = this.querySelector('[data-mq-first-name]');
      if (input && document.activeElement !== input) input.value = text;
      this.nameCount = this.state.unsplit ? 1 : Math.max(Logic.splitNames(text).length, 1);
      this.revealed = Boolean(text.trim()) || this.hasPhotos();
      var reveal = this.querySelector('[data-mq-reveal]');
      if (reveal) reveal.hidden = !this.revealed;
    }

    hasPhotos() {
      return this.petList().some(function (p) { return p.photos.length; });
    }

    setPetCount(count) {
      var self = this;
      count = Math.max(1, Math.min(MAX_PETS, count | 0));
      if (count === this.numPets) return;
      var from = this.numPets;
      this.extra.forEach(function (p, i) {
        if (p.position <= count) return;
        p.photos.forEach(function (photo) { if (photo.url && photo.file) { try { URL.revokeObjectURL(photo.url); } catch (e) { /* ignore */ } } });
        clearTimeout(p.sketch.timer);
        self.extra[i] = extraPet(p.position);
      });
      this.numPets = count;
      track('PetCount', { num_pets: count });
      beacon('pets', { props: this.petVia ? { from: from, to: count, via: this.petVia } : { from: from, to: count } });
      if (count === 1) this.fillSingleInputs();
      if (this.state.token || count > 1) this.syncCount().catch(function () { /* retried on the next upload */ });
      this.buildPets();
      this.persist();
      this.renderPhotos();
      this.render();
    }

    /* Tell the draft how many pets there are, once per change. Photos for pets 2 to 5
       need their pet to exist on the draft first. */
    syncCount() {
      var self = this;
      var count = this.numPets;
      return this.ensureDraft().then(function (token) {
        if (self.countFor === count && self.countPromise) return self.countPromise;
        self.countFor = count;
        self.countPromise = fetch(self.api + '/drafts/' + encodeURIComponent(token) + '/pets', {
          method: 'PUT', mode: 'cors', credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count: count })
        }).then(function (r) {
          if (!r.ok) throw new Error('pets ' + r.status);
          return token;
        }).catch(function (err) {
          self.countFor = 0;
          self.countPromise = null;
          throw err;
        });
        return self.countPromise;
      });
    }

    fillSingleInputs() {
      var map = { petName: '[data-mq-pet-name]', breed: '[data-mq-breed]', note: '[data-mq-note]' };
      Object.keys(map).forEach(function (k) {
        var el = this.querySelector(map[k]);
        if (el) el.value = this.state[k] || '';
      }, this);
    }

    /* Builds the cards on the photo step, the per-pet fields on the pet step and the
       sketch frames. Only when the number of pets changes, so typing never loses focus;
       syncPets() keeps them up to date after that. */
    buildPets() {
      var multi = this.multi();
      /* Styles that only apply with several pets hang off this class, so one pet is untouched. */
      this.classList.toggle('is-multi', multi);
      this.querySelectorAll('[data-mq-single-only]').forEach(function (el) { el.hidden = multi; });
      this.querySelectorAll('[data-mq-multi-only]').forEach(function (el) { el.hidden = !multi; });
      var full = this.numPets >= MAX_PETS;
      this.querySelectorAll('[data-mq-pet-add]').forEach(function (add) { add.hidden = full; });
      this.showBlocked = false;
      var blocked = this.querySelector('[data-mq-blocked]');
      if (blocked) { blocked.hidden = true; blocked.textContent = ''; }

      var cards = this.querySelector('[data-mq-pet-cards]');
      var extras = this.querySelector('[data-mq-pet-extras]');
      var sketches = this.querySelector('[data-mq-sketches]');
      if (!multi) {
        if (cards) cards.innerHTML = '';
        if (extras) extras.innerHTML = '';
        if (sketches) sketches.innerHTML = '';
        var oldDots = this.querySelector('[data-mq-sketch-dots]');
        if (oldDots) oldDots.hidden = true;
        /* Back to one pet: the size cards as they always were, no per-pet rows. */
        var sizeCards = this.querySelector('[data-mq-size-cards]');
        if (sizeCards) sizeCards.hidden = false;
        var petSizes = this.querySelector('[data-mq-pet-sizes]');
        if (petSizes) { petSizes.hidden = true; petSizes.innerHTML = ''; petSizes.dataset.html = ''; }
        return;
      }
      var d = this.dataset;
      var pets = this.petList();

      if (cards) {
        cards.innerHTML = pets.map(function (pet) {
          var n = pet.position;
          return '<section class="mq-petcard" data-mq-pet="' + n + '">' +
            '<header class="mq-petcard__head">' +
              '<span class="mq-petcard__check" aria-hidden="true"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l4.5 4.5L19 7.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
              '<h3 class="mq-petcard__title" data-mq-card-title></h3>' +
              /* Every figurine in a set is 20% off: said on each card, where the pets are. */
              (d.txtPetDeal ? '<span class="mq-petcard__deal">' + escapeHtml(d.txtPetDeal) + '</span>' : '') +
              '<span class="mq-petcard__count" data-mq-card-count></span>' +
              '<button type="button" class="mq-petcard__remove" data-mq-pet-remove="' + n + '" aria-label="' + escapeHtml(d.txtPetRemove) + '" title="' + escapeHtml(d.txtPetRemove) + '">' +
                '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" stroke-linecap="round"/></svg></button>' +
            '</header>' +
            '<p class="mq-petcard__hint">' + escapeHtml((d.txtCardHint || '').replace('{min}', this.minPhotos)) + '</p>' +
            '<ul class="mq-photos mq-photos--card" data-mq-card-photos></ul>' +
            '<div class="mq-check" data-mq-check-note hidden></div>' +
            '<input type="file" accept="image/*" multiple class="visually-hidden-mq" data-mq-card-add tabindex="-1" aria-hidden="true">' +
          '</section>';
        }, this).join('');
      }

      if (extras) {
        extras.innerHTML = pets.map(function (pet) {
          var n = pet.position;
          return '<div class="mq-petextra" data-mq-pet="' + n + '">' +
            '<div class="mq-petextra__head"><span class="mq-petextra__photo" data-mq-card-photo></span>' +
              '<p class="mq-petextra__h" data-mq-card-label></p></div>' +
            '<label class="mq-field"><span>' + escapeHtml(d.txtBreed) + '</span>' +
              '<input type="text" autocomplete="off" maxlength="120" data-mq-card-breed value="' + escapeHtml(pet.breed) + '"></label>' +
            '<label class="mq-field"><span data-mq-card-note-label></span>' +
              '<textarea rows="2" maxlength="2000" data-mq-card-note>' + escapeHtml(pet.note) + '</textarea></label>' +
          '</div>';
        }).join('');
      }

      if (sketches) {
        /* The single sketch frame, copied once per pet without its hooks. */
        var single = this.querySelector('[data-mq-sketch]');
        sketches.innerHTML = '';
        pets.forEach(function (pet) {
          var fig = single ? single.cloneNode(true) : document.createElement('figure');
          fig.className = 'mq-sketch mq-sketch--mini';
          fig.removeAttribute('hidden');
          [fig].concat(Array.from(fig.querySelectorAll('*'))).forEach(function (el) {
            Array.from(el.attributes).forEach(function (a) { if (/^data-mq-/.test(a.name)) el.removeAttribute(a.name); });
          });
          fig.dataset.mqPet = pet.position;
          var name = document.createElement('p');
          name.className = 'mq-sketch__name';
          fig.insertBefore(name, fig.firstChild);
          var note = fig.querySelector('.mq-sketch__note');
          if (note) note.remove();
          sketches.appendChild(fig);
        });
        /* A carousel: one sketch at a time, full width, dots under it. */
        var dots = this.querySelector('[data-mq-sketch-dots]');
        if (!dots) {
          dots = document.createElement('div');
          dots.className = 'mq-sketch-dots';
          dots.setAttribute('data-mq-sketch-dots', '');
          sketches.parentNode.insertBefore(dots, sketches.nextSibling);
        }
        dots.hidden = false;
        dots.innerHTML = pets.map(function (pet, i) {
          return '<button type="button" data-mq-sketch-dot="' + i + '" aria-label="' + escapeHtml(this.petLabel(pet)) + '"></button>';
        }, this).join('');
        sketches.scrollLeft = 0;
        this.syncSketchDots();
      }
      this.syncPets();
    }

    syncPets() {
      if (!this.multi()) return;
      var self = this;
      var d = this.dataset;
      this.petList().forEach(function (pet) {
        var label = self.petLabel(pet);
        var card = self.querySelector('[data-mq-pet-cards] [data-mq-pet="' + pet.position + '"]');
        if (card) {
          var ok = pet.photos.filter(function (p) { return p.state === 'ok'; }).length;
          var busy = pet.photos.some(function (p) { return p.state !== 'ok'; });
          card.querySelector('[data-mq-card-title]').textContent = (d.txtCardTitle || '{name}').replace('{name}', label);
          var count = card.querySelector('[data-mq-card-count]');
          count.textContent = pet.photos.length + ' / ' + self.minPhotos;
          count.classList.toggle('is-ok', pet.photos.length >= self.minPhotos);
          /* Enough photos, but not of the pet: the card's check turns amber instead of green. */
          var blocked = self.checkCurrent(pet) && pet.check.level === 'block' && pet.sketch.status !== 'ready';
          card.classList.toggle('is-ready', !busy && ok >= self.minPhotos && !blocked);
          card.classList.toggle('is-blocked', !busy && ok >= self.minPhotos && blocked);
          /* Only when something changed: re-rendering would reload the thumbnails. */
          var list = card.querySelector('[data-mq-card-photos]');
          /* Up to the minimum, then always one empty box to add another, until the maximum. */
          var html = self.photosHtml(pet.photos, Math.min(MAX_PHOTOS, Math.max(self.minPhotos, pet.photos.length + 1)), true, pet);
          if (list.dataset.html !== html) { list.innerHTML = html; list.dataset.html = html; }
        }
        var extra = self.querySelector('[data-mq-pet-extras] [data-mq-pet="' + pet.position + '"]');
        if (extra) {
          extra.querySelector('[data-mq-card-label]').textContent = label;
          /* Their own photo next to the fields, so it is clear which pet gets which name. */
          var first = pet.photos.find(function (p) { return p.state === 'ok' && p.url; });
          var holder = extra.querySelector('[data-mq-card-photo]');
          var img = holder.querySelector('img');
          if (first && (!img || img.getAttribute('src') !== first.url)) {
            if (!img) { img = document.createElement('img'); img.alt = ''; holder.appendChild(img); }
            img.src = first.url;
          }
          extra.querySelector('[data-mq-card-note-label]').textContent = (d.txtNote || '').replace('{name}', label);
        }
      });
      /* Why "Verder" does not go on: only after they tapped it, never as a greeting. */
      var blocked = this.querySelector('[data-mq-blocked]');
      if (blocked) {
        var problem = this.showBlocked ? this.petsProblem('photos') : '';
        if (!problem) this.showBlocked = false;
        blocked.textContent = problem;
        blocked.hidden = !problem;
      }
      this.renderSketches();
    }

    firstUnreadyCard() {
      var cards = Array.from(this.querySelectorAll('[data-mq-pet-cards] [data-mq-pet]'));
      return cards.find(function (card) { return !card.classList.contains('is-ready'); }) || null;
    }

    petsProblem(part) {
      var d = this.dataset;
      return Logic.petsProblem(this.petList().map(function (pet) {
        return {
          position: pet.position,
          name: pet.name,
          ok: pet.photos.filter(function (p) { return p.state === 'ok'; }).length,
          sending: pet.photos.some(function (p) { return p.state === 'sending'; }),
          failed: pet.photos.some(function (p) { return p.state === 'fail'; })
        };
      }), this.minPhotos, {
        petN: d.txtPetN || 'Pet {n}',
        failed: d.errUpload || 'One photo did not arrive.',
        uploading: d.txtUploading || '{name}',
        photos: d.txtNeedPhotos || '{name}',
        name: d.txtNeedName || '{n}'
      }, part);
    }

    renderSketches() {
      var self = this;
      this.querySelectorAll('[data-mq-sketches] [data-mq-pet]').forEach(function (fig) {
        var pet = self.pet(Number(fig.dataset.mqPet));
        if (!pet) return;
        var sk = pet.sketch;
        var pending = sk.status === 'pending';
        var hasImage = Boolean(sk.url) && !pending;
        fig.dataset.state = sk.status;
        fig.querySelector('.mq-sketch__name').textContent = self.petLabel(pet) + ' · ' + pet.position + '/' + self.numPets;
        var wait = fig.querySelector('.mq-sketch__wait');
        if (wait) wait.hidden = !(pending || sk.status === 'none');
        var line = fig.querySelector('.mq-sketch__line');
        if (line) line.textContent = ((self.dataset.txtWait || '').split('|')[0] || '').replace('{name}', self.petLabel(pet));
        var frame = fig.querySelector('.mq-sketch__frame');
        if (frame) frame.hidden = !hasImage;
        var fail = fig.querySelector('.mq-sketch__fail:not(.mq-sketch__blocked-text)');
        if (fail) fail.hidden = !(sk.status === 'failed' && !hasImage);
        var blocked = fig.querySelector('.mq-sketch__blocked');
        if (blocked) {
          blocked.hidden = sk.status !== 'blocked';
          blocked.querySelector('.mq-sketch__blocked-text').textContent = (self.dataset.txtSketchBlocked || '').replace(/\{name\}/g, self.petLabel(pet));
        }
        var img = fig.querySelector('img');
        if (img && hasImage && img.getAttribute('src') !== sk.url) img.src = sk.url;
        var tools = fig.querySelector('.mq-sketch__tools');
        if (tools) tools.hidden = !hasImage;
      });
    }

    syncSketchDots() {
      var track = this.querySelector('[data-mq-sketches]');
      var dots = this.querySelector('[data-mq-sketch-dots]');
      if (!track || !dots) return;
      /* The dots are drawn while the sketch step is still display:none, so the carousel
         has no width yet and scrollLeft / 0 is not a number. Nothing syncs them again on
         the way in, which left the first sketch showing with no pill lit. Unscrolled is
         the first one. */
      var index = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
      dots.querySelectorAll('[data-mq-sketch-dot]').forEach(function (dot, i) {
        dot.classList.toggle('is-on', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    setSizeMode(mode) {
      this.state.sizeMode = mode === 'each' ? 'each' : 'same';
      if (this.state.sizeMode === 'each') {
        /* Start every pet at the size chosen for all, so nothing jumps. */
        this.extra.forEach(function (pet) { if (!pet.size) pet.size = this.state.size; }, this);
      }
      beacon('size_mode', { props: { mode: this.state.sizeMode, num_pets: this.numPets } });
      this.persist();
      this.syncSummary();
      this.syncNext();
    }

    setPetSize(position, key) {
      if (!this.sizes[key]) return;
      if (position === 1) {
        this.state.size = key;
        var radio = this.querySelector('[name="mq-size"][value="' + key + '"]');
        if (radio) radio.checked = true;
      } else if (this.pet(position)) {
        this.pet(position).size = key;
      }
      this.persist();
      this.syncSummary();
      this.syncNext();
    }

    /* The per-pet size rows and the link that switches between one size and a size per pet. */
    syncSizeMode() {
      var each = this.multi() && this.state.sizeMode === 'each';
      var d = this.dataset;
      var link = this.querySelector('[data-mq-size-mode]');
      if (link) link.textContent = each ? (d.txtSizeSame || '') : (d.txtSizeEach || '');
      var cards = this.querySelector('[data-mq-size-cards]');
      if (cards) cards.hidden = each;
      var box = this.querySelector('[data-mq-pet-sizes]');
      if (box) {
        box.hidden = !each;
        var html = each ? this.petList().map(function (pet) {
          var chosen = this.sizeKeyOf(pet);
          return '<div class="mq-petsize" data-mq-size-pet="' + pet.position + '">' +
            '<span class="mq-petsize__name">' + escapeHtml(this.petLabel(pet)) + '</span>' +
            '<span class="mq-petsize__opts">' + Object.keys(this.sizes).map(function (key) {
              var size = this.sizes[key];
              var on = key === chosen;
              return '<button type="button" class="mq-petsize__opt' + (on ? ' is-on' : '') + '" data-mq-pet-size="' + escapeHtml(key) + '" aria-pressed="' + on + '">' +
                '<b>' + escapeHtml(size.label.split(' (')[0]) + '</b><span>' + escapeHtml(key.replace('cm', ' cm')) + ' · ' + money(size.price, d.currency) + '</span></button>';
            }, this).join('') + '</span></div>';
          }, this).join('') : '';
        if (box.dataset.html !== html) { box.innerHTML = html; box.dataset.html = html; }
      }
      this.syncSetPreview();
    }

    /* Several pets on the size step: all their figurines next to the mug, each at its size.
       The stock figurine stands in at once and gives way to theirs as soon as it has loaded:
       never a loader on the step where the prices are. */
    syncSetPreview() {
      var row = this.querySelector('[data-mq-set-row]');
      if (!row || !this.multi()) return;
      var self = this;
      var d = this.dataset;
      var refCm = parseInt(d.refCm || '9', 10);
      var items = ['<div class="mq-scale__item mq-scale__item--ref"><div class="mq-scale__fig" style="--mq-cm: ' + refCm + '"><span class="mq-mug" aria-hidden="true"></span></div>' +
        '<p class="mq-scale__name">' + escapeHtml(d.txtMug || '') + '</p><p class="mq-scale__meta">' + escapeHtml(d.txtMugNote || '') + '</p></div>'];
      this.petList().forEach(function (pet) {
        var key = self.sizeKeyOf(pet);
        var cm = parseInt(key, 10) || 9;
        var sk = pet.sketch;
        /* A cut-out we know of (fetched now, or restored with the draft) is loaded before it
           is shown, so the loader gives way to the finished image. */
        if (sk.figureUrl && !sk.figureShown && !sk.figureFailed && sk.probing !== sk.figureUrl) {
          sk.probing = sk.figureUrl;
          var probe = new Image();
          probe.onload = function () { if (sk.probing === probe.src) { sk.figureShown = true; self.syncSetPreview(); } };
          probe.onerror = function () { sk.figureFailed = true; self.syncSetPreview(); };
          probe.src = sk.figureUrl;
        }
        /* Theirs only once it has loaded, so the swap never shows a half-drawn image. */
        var theirs = !!(sk.figureShown && sk.figureUrl);
        var src = theirs ? sk.figureUrl : d.figureStock || '';
        items.push('<div class="mq-scale__item mq-set__pet">' +
          '<span class="mq-scale__fig" style="--mq-cm: ' + cm + '"><img src="' + escapeHtml(src) + '" alt="" draggable="false"' + (theirs ? ' class="is-theirs"' : '') + '></span>' +
          '<span class="mq-scale__name">' + escapeHtml(self.petLabel(pet)) + '</span>' +
          '<span class="mq-scale__meta">' + cm + ' cm</span></div>');
      });
      var html = items.join('');
      if (row.dataset.html !== html) { row.innerHTML = html; row.dataset.html = html; }
    }

    /* Cut-outs for pets 2 to 5; pet 1 uses fetchFigure(), as with a single pet. */
    fetchFigureFor(position) {
      var self = this;
      var pet = this.pet(position);
      if (!pet || position === 1) return;
      var sk = pet.sketch;
      if (sk.figureUrl || sk.figureFailed || sk.status !== 'ready' || (sk.figureTries || 0) >= FIGURE_TRIES || sk.figureTimer || !this.state.token) return;
      sk.figureTimer = setTimeout(function () {
        sk.figureTimer = null;
        sk.figureTries = (sk.figureTries || 0) + 1;
        fetch(self.sketchUrl(position), { mode: 'cors', credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (body) {
            var current = self.pet(position);
            if (body && body.figure_url && current && current.sketch === sk) {
              sk.figureUrl = self.apiOrigin() + body.figure_url;
            } else if (body && body.figure_status === 'failed' && current && current.sketch === sk) {
              sk.figureFailed = true;
            } else {
              self.fetchFigureFor(position);
            }
            self.syncSetPreview();
          });
      }, sk.figureTries ? 4000 : 0);
    }

    /* The size key of one pet: the order's size, unless sizes are chosen per pet. */
    sizeKeyOf(pet) {
      if (!this.multi() || this.state.sizeMode !== 'each' || pet.position === 1) return this.state.size;
      return pet.size || this.state.size;
    }

    petSizes() {
      return this.petList().map(function (pet) { return this.sizes[this.sizeKeyOf(pet)]; }, this);
    }

    /* Price lines with several pets: one per pet with its size, the order discount, the
       total. The same arithmetic as the automatic discount in Shopify, so checkout agrees. */
    priceHtml() {
      var self = this;
      var currency = this.dataset.currency;
      var sizes = this.petSizes();
      var price = Logic.pricingMixed(sizes.map(function (size) { return size ? size.price : 0; }));
      var pets = this.petList();
      return '<ul class="mq-priceview__lines">' + price.lines.map(function (line) {
        var size = sizes[line.position - 1];
        return '<li><span>' + escapeHtml(self.petLabel(pets[line.position - 1])) + ' · ' + escapeHtml(size ? size.label : '-') + '</span><span>' + money(line.full, currency) + '</span></li>';
      }).join('') + '</ul>' +
        '<p class="mq-priceview__row mq-priceview__row--discount"><span>' + escapeHtml(this.dataset.txtDiscount) + '</span><span>−' + money(price.discount, currency) + '</span></p>' +
        '<p class="mq-priceview__row mq-priceview__row--total"><span>' + escapeHtml(this.dataset.txtTotal) + '</span><b>' + money(price.total, currency) + '</b></p>';
    }

    /* The discount on its own line, then the total. Several pets only. */
    totalHtml(withToday) {
      var currency = this.dataset.currency;
      var price = Logic.pricingMixed(this.petSizes().map(function (size) { return size ? size.price : 0; }));
      /* On the size step, a deposit order also says what is due today: that is the number
         that decides. The last step has its own table for it. */
      var split = withToday && this.depositMode() ? this.split() : null;
      return '<p class="mq-priceview__row mq-priceview__row--discount"><span>' + escapeHtml(this.dataset.txtDiscount) + '</span><span>−' + money(price.discount, currency) + '</span></p>' +
        '<p class="mq-priceview__row mq-priceview__row--total"><span>' + escapeHtml(this.dataset.txtTotal) + '</span><b>' + money(price.total, currency) + '</b></p>' +
        (split ? '<p class="mq-priceview__row mq-priceview__row--today"><span>' + escapeHtml(this.dataset.txtDueNow || 'Today') + '</span><b>' + money(split.now, currency) + '</b></p>' : '');
    }

    /* What the order costs, in cents: one figure, or all of them after the discount. */
    orderCents(size) {
      if (!size) return 0;
      if (!this.multi()) return size.price;
      return Logic.pricingMixed(this.petSizes().map(function (s) { return s ? s.price : 0; })).total;
    }

    /* A deposit at checkout, the rest at approval: on when every size has a deposit variant. */
    depositMode() {
      var keys = Object.keys(this.sizes);
      return keys.length > 0 && keys.every(function (key) {
        return this.sizes[key].depositVariant && this.sizes[key].deposit > 0;
      }, this);
    }

    /* What they pay at checkout: the deposit unless they chose to pay it all on the last
       step. The size step keeps showing the deposit: that choice is still theirs to make. */
    payDeposit() {
      return this.depositMode() && !this.state.payFull;
    }

    /* Today, later and the total for the pets and sizes as chosen now. */
    split() {
      var sizes = this.multi() ? this.petSizes() : [this.sizes[this.state.size]];
      if (!sizes.length || sizes.some(function (size) { return !size; })) return null;
      return Logic.depositSplit(sizes.map(function (s) { return s.price; }),
                                sizes.map(function (s) { return s.deposit; }));
    }

    /* The last step's receipt: the whole price, the part after approval, and under the line
       what is due today. Without a deposit it is one line, the whole price today. Several
       pets show their total in the list above it, so the receipt leaves it out. */
    syncPlan() {
      var plan = this.querySelector('[data-mq-plan]');
      if (!plan) return;
      this.syncPayChoice();
      var split = this.payDeposit() ? this.split() : null;
      var size = this.sizes[this.state.size];
      var currency = this.dataset.currency;
      var total = split ? split.total : (size ? this.orderCents(size) : 0);
      var put = function (sel, cents) {
        var el = plan.querySelector(sel);
        if (el) el.textContent = size ? money(cents, currency) : '';
      };
      put('[data-mq-plan-total]', total);
      put('[data-mq-plan-later]', split ? split.later : 0);
      put('[data-mq-plan-now]', split ? split.now : total);
      plan.querySelector('[data-mq-plan-total-row]').hidden = !split || this.multi();
      plan.querySelector('[data-mq-plan-later-row]').hidden = !split;
      plan.classList.toggle('is-single', !split);
    }

    /* Deposit or everything now, deposit first. Each option carries its own amount today. */
    syncPayChoice() {
      var box = this.querySelector('[data-mq-pay]');
      if (!box) return;
      var offered = this.depositMode();
      var split = offered ? this.split() : null;
      box.hidden = !split;
      if (!split) return;
      var currency = this.dataset.currency;
      var full = Boolean(this.state.payFull);
      box.querySelectorAll('input[name="mq-pay"]').forEach(function (input) {
        input.checked = (input.value === 'full') === full;
      });
      box.querySelector('[data-mq-pay-deposit]').textContent = money(split.now, currency);
      box.querySelector('[data-mq-pay-full]').textContent = money(split.total, currency);
      this.querySelectorAll('[data-mq-refund-deposit]').forEach(function (el) { el.hidden = full; });
      this.querySelectorAll('[data-mq-refund-full]').forEach(function (el) { el.hidden = !full; });
    }

    syncDue() {
      var split = this.depositMode() ? this.split() : null;
      var short = this.querySelector('[data-mq-due-short]');
      if (short && split && short.dataset.dueTemplate) {
        if (!short.dataset.plain) short.dataset.plain = short.textContent;
        short.textContent = short.dataset.dueTemplate.replace('{now}', money(split.now, this.dataset.currency));
      } else if (short && short.dataset.plain) {
        short.textContent = short.dataset.plain;
      }
      /* Every size card says what that size costs today: 20% off each deposit from two pets. */
      var multi = this.multi();
      var template = this.dataset.txtTodayCard || '{amount}';
      this.querySelectorAll('[data-mq-today]').forEach(function (el) {
        var size = this.sizes[el.dataset.mqToday];
        var show = Boolean(split && size && size.deposit);
        el.hidden = !show;
        if (show) {
          var cents = multi ? size.deposit - Math.round(size.deposit * 20 / 100) : size.deposit;
          el.textContent = template.replace('{amount}', money(cents, this.dataset.currency));
        }
      }, this);
      this.syncPlan();
    }

    /* ---------------- open / close ---------------- */

    open(files, from, opener) {
      if (!this.classList.contains('is-open')) {
        beacon('studio_open', { props: { from: opener || (from === 'email' ? 'email' : 'page') } });
        track('StartUpload', { from: from || (files && files.length ? 'upload' : 'cta') });
        this.dropOtherCart();
      }
      this.classList.add('is-open');
      /* Lock the page underneath without losing its place: iOS ignores overflow on
         <html> and would scroll the page to the bottom behind the overlay. */
      this.savedScroll = window.scrollY || 0;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = -this.savedScroll + 'px';
      document.body.style.width = '100%';
      /* New files always land on the photo step; otherwise pick up where they left off. */
      this.stepIndex = files && files.length ? 0 : this.resumeStep();
      this.render();
      if (files && files.length) {
        this.addFiles(files);
        this.syncNameField();
      }
      /* A new visitor starts with one question; the keyboard opens with it. */
      var nameInput = this.querySelector('[data-mq-first-name]');
      if (nameInput && this.stepIndex === 0 && !this.state.nameText) {
        try { nameInput.focus({ preventScroll: true }); } catch (e) { nameInput.focus(); }
      }
    }

    close() {
      this.lastStepView = '';
      flush();
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      try { window.scrollTo({ top: this.savedScroll || 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, this.savedScroll || 0); }
      this.reconcileCart();
    }

    /* ---------------- draft on the service ---------------- */

    fields() {
      var consent = this.querySelector('[data-mq-consent]');
      return {
        email: this.state.email, phone: this.state.phone, pet_name: this.state.petName,
        /* Which line stood under the e-mail field: it decides which follow-up mails they get. */
        email_consent: (consent && consent.getAttribute('data-mq-consent')) || null,
        breed: this.state.breed, note: this.state.note,
        stance_key: this.state.stance, size_key: this.state.size,
        context: this.dataset.context || null, locale: this.dataset.locale || 'en'
      };
    }

    ensureDraft() {
      var self = this;
      if (this.state.token) return Promise.resolve(this.state.token);
      if (this.creating) return this.creating;
      this.creating = fetch(this.api + '/drafts', {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.fields())
      }).then(function (r) {
        if (!r.ok) throw new Error('draft ' + r.status);
        return r.json();
      }).then(function (draft) {
        self.state.token = draft.token;
        self.creating = null;
        self.persist();
        return draft.token;
      }).catch(function (err) {
        self.creating = null;
        throw err;
      });
      return this.creating;
    }

    save() {
      var self = this;
      if (!this.state.token) return this.ensureDraft();
      return fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token), {
        method: 'PATCH', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.fields())
      }).then(function (r) {
        if (r.status === 404 || r.status === 409) {
          /* The draft is gone or already ordered: start clean rather than fight it. */
          self.state.token = '';
          self.countFor = 0;
          self.photos = [];
          self.extra.forEach(function (p) { p.photos = []; });
          self.renderPhotos();
          self.resetSketch();
          return self.ensureDraft();
        }
        if (!r.ok) return null;
        if (!self.multi()) return r.json();
        /* Pet 1 went along with the draft; pets 2 to 5 have their own address. */
        return self.syncCount().then(function (token) {
          return Promise.all(self.extra.slice(0, self.numPets - 1).map(function (pet) {
            return fetch(self.api + '/drafts/' + encodeURIComponent(token) + '/pets/' + pet.position, {
              method: 'PATCH', mode: 'cors', credentials: 'omit',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                pet_name: pet.name, breed: pet.breed, note: pet.note,
                size_key: self.state.sizeMode === 'each' && pet.size ? pet.size : ''
              })
            });
          }));
        });
      }).catch(function () { return null; });
    }

    addFiles(files, position) {
      var self = this;
      position = position || 1;
      var pet = this.pet(position);
      var room = MAX_PHOTOS - pet.photos.length;
      files.slice(0, Math.max(room, 0)).forEach(function (file) {
        var entry = { id: '', name: file.name, url: '', state: 'sending', file: file };
        /* A HEIC thumbnail only renders in Safari; elsewhere the slot waits for the
           JPEG the service sends back after upload. */
        if (!isHeic(file)) {
          try { entry.url = URL.createObjectURL(file); } catch (e) { /* ignore */ }
        }
        pet.photos.push(entry);
        self.upload(entry, position);
      });
      this.renderPhotos();
      this.syncNext();
    }

    upload(entry, position) {
      var self = this;
      position = position || 1;
      var pet = this.pet(position);
      entry.state = 'sending';
      this.renderPhotos();
      (this.multi() ? this.syncCount() : this.ensureDraft()).then(function (token) {
        return shrink(entry.file).then(function (file) {
          if (!entry.url && !isHeic(file)) {
            try { entry.url = URL.createObjectURL(file); } catch (e) { /* ignore */ }
            self.renderPhotos();
          }
          var body = new FormData();
          body.append('file', file, file.name);
          if (position > 1) body.append('pet', String(position));
          return fetch(self.api + '/drafts/' + encodeURIComponent(token) + '/photos', {
            method: 'POST', body: body, mode: 'cors', credentials: 'omit'
          });
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('upload ' + r.status);
        return r.json();
      }).then(function (res) {
        entry.id = String(res.photo.id);
        entry.state = 'ok';
        if (!entry.url && res.photo.url) entry.url = self.apiOrigin() + res.photo.url;
        self.photosChanged(position);
        beacon('photo_uploaded', { props: { heic: /hei[cf]/i.test(entry.name) } });
        if (!self.uploadTracked && pet.photos.filter(function (p) { return p.state === 'ok'; }).length === self.minPhotos) {
          self.uploadTracked = true;
          track('UploadComplete', { photos: self.minPhotos });
        }
      }).catch(function () {
        entry.state = 'fail';
      }).then(function () {
        self.renderPhotos();
        self.syncNext();
      });
    }

    removePhoto(key, position) {
      var photos = this.pet(position || 1).photos;
      var index = photos.findIndex(function (p) { return (p.id || p.name) === key; });
      if (index < 0) return;
      var entry = photos[index];
      photos.splice(index, 1);
      this.photosChanged(position || 1);
      this.renderPhotos();
      this.syncNext();
      if (entry.id && this.state.token) {
        fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token) + '/photos/' + entry.id, {
          method: 'DELETE', mode: 'cors', credentials: 'omit'
        }).catch(function () { /* the webhook only copies what is still there */ });
      }
      if (entry.url) { try { URL.revokeObjectURL(entry.url); } catch (e) { /* ignore */ } }
    }

    /* Thumbnails plus empty slots up to `slots`; the first ones up to the minimum are marked. */
    photosHtml(photos, slots, card, pet) {
      var sending = this.dataset.txtSending || '';
      var remove = this.dataset.txtRemove || '';
      /* Which photos do not count, said on the photo. A photo with no animal at all is
         always marked (calibration: never wrongly); a barely visible one only when that
         is why there is no sketch. Nothing once the sketch is made. */
      var check = pet && pet.check;
      var marks = {};
      if (check && this.checkCurrent(pet) && pet.sketch.status !== 'ready') {
        Object.keys(check.photos || {}).forEach(function (id) {
          if (check.photos[id] === 'none' || (check.level === 'block' && check.photos[id] === 'unclear')) marks[id] = check.photos[id];
        });
      }
      var d = this.dataset;
      var html = photos.map(function (p) {
        var key = p.id || p.name;
        var mark = p.state === 'ok' && marks[p.id] && marks[p.id] !== 'ok' ? (marks[p.id] === 'none' ? d.txtCheckNone : d.txtCheckUnclear) : '';
        return '<li class="mq-photo" data-state="' + p.state + '">' +
          (p.url ? '<img src="' + p.url + '" alt="">' : '<span class="mq-photo__blank"></span>') +
          (p.state === 'sending' ? '<span class="mq-photo__badge">' + sending + '</span>' : '') +
          (mark ? '<span class="mq-photo__badge mq-photo__badge--warn" title="' + escapeHtml(mark) + '">!</span>' : '') +
          (p.state === 'fail' ? '<span class="mq-photo__badge mq-photo__badge--fail">!</span>' : '') +
          '<button type="button" class="mq-photo__remove" data-mq-remove="' + escapeHtml(key) + '" aria-label="' + remove + '">&times;</button>' +
          '</li>';
      });
      /* The boxes are the upload button: the first empty one says so. */
      var pick = escapeHtml(this.dataset.txtPhotosPick || '+');
      for (var i = photos.length; i < slots; i++) {
        var required = i < this.minPhotos;
        var first = i === photos.length;
        html.push('<li class="mq-photo mq-photo--empty' + (required ? ' mq-photo--required' : '') + (first ? ' mq-photo--pick' : '') + '" ' +
          (card ? 'data-mq-card-slot' : 'data-mq-slot') + ' role="button" tabindex="0">' +
          (first ? '<span class="mq-photo__plus">+</span><span class="mq-photo__pick">' + pick + '</span>'
            : required ? '<span class="mq-photo__num">' + (i + 1) + '</span>' : '<span class="mq-photo__plus">+</span>') + '</li>');
      }
      return html.join('');
    }

    renderPhotos() {
      var list = this.querySelector('[data-mq-photos]');
      var count = this.querySelector('[data-mq-photos-count]');
      var bar = this.querySelector('[data-mq-photos-bar]');
      var addWrap = this.querySelector('[data-mq-add-wrap]');
      this.syncPets();
      if (!list) return;
      var self = this;
      list.innerHTML = this.photosHtml(this.photos, MAX_PHOTOS, false, this.pet(1));
      var ok = this.photos.filter(function (p) { return p.state === 'ok'; }).length;
      var total = this.photos.length;
      if (count) {
        var tpl = total < this.minPhotos ? (this.dataset.txtCountMin || '{n} / {min}') : (this.dataset.txtCount || '{n} / {max}');
        count.textContent = tpl.replace('{n}', total).replace('{min}', this.minPhotos).replace('{max}', MAX_PHOTOS);
        count.classList.toggle('is-ok', total >= this.minPhotos);
      }
      if (bar) bar.style.width = Math.min(100, Math.round((total / this.minPhotos) * 100)) + '%';
      this.syncCheck();
      this.syncRestart();
      if (addWrap) addWrap.hidden = this.photos.length >= MAX_PHOTOS;
      /* Tapping an empty slot opens the picker too. */
      list.querySelectorAll('[data-mq-slot]').forEach(function (slot) {
        slot.addEventListener('click', function () { var input = self.querySelector('[data-mq-add-photos]'); if (input) input.click(); });
      });
    }

    /* ---------------- AI sketch ---------------- */

    apiOrigin() {
      try { return new URL(this.api).origin; } catch (e) { return ''; }
    }

    resetSketch() {
      clearTimeout(this.sketch.timer);
      this.sketch = emptySketch();
      this.figureShown = '';
      this.figureTries = 0;
      this.applyFigure();
      this.extra.forEach(function (p) { clearTimeout(p.sketch.timer); p.sketch = emptySketch(); });
      clearTimeout(this.check.timer);
      this.check = emptyCheck();
      this.extra.forEach(function (p) { clearTimeout(p.check.timer); p.check = emptyCheck(); });
      this.syncCheck();
      this.renderSketch();
    }

    /* ---------------- the look at the photos ----------------
       A moment after the photos settle, the service says what they show. Nothing here
       stops anyone: at worst there is no sketch, and the order goes on. */

    photosChanged(position) {
      var pet = this.pet(position);
      pet.check.redo = '';
      /* No sketch because of the photos: other photos deserve another try. */
      if (pet.sketch.status === 'blocked') { clearTimeout(pet.sketch.timer); pet.sketch = emptySketch(); this.renderSketch(); }
      this.scheduleCheck(position);
    }

    scheduleCheck(position) {
      var self = this;
      var pet = this.pet(position);
      clearTimeout(pet.check.timer);
      pet.check.timer = setTimeout(function () { pet.check.timer = null; self.runCheck(position); }, CHECK_DELAY_MS);
      this.syncCheck();
    }

    /* The verdict is in and it is about the photos the pet has now. A verdict about an
       earlier set says nothing: photos changed since, and a new look is on its way. */
    checkCurrent(pet) {
      return pet.check.status === 'ready' && pet.check.fp === this.photoKey(pet);
    }

    /* A look is due or under way for photos that could give a sketch. */
    checkBusy(pet) {
      var ok = pet.photos.filter(function (p) { return p.state === 'ok' && p.id; }).length;
      return ok >= this.minPhotos && !this.checkCurrent(pet) && pet.photos.every(function (p) { return p.state !== 'fail'; });
    }

    photoKey(pet) {
      return pet.photos.filter(function (p) { return p.state === 'ok' && p.id; }).map(function (p) { return p.id; }).sort().join(',');
    }

    runCheck(position) {
      var self = this;
      var pet = this.pet(position);
      var check = pet.check;
      if (pet.photos.some(function (p) { return p.state === 'sending'; })) return; /* comes back when it lands */
      var key = this.photoKey(pet);
      if (key.split(',').filter(Boolean).length < this.minPhotos || !this.state.token) {
        if (check.status !== 'none') { pet.check = emptyCheck(); this.syncCheck(); this.renderPhotos(); }
        return;
      }
      if (check.fp === key && check.status !== 'none') return;
      check.status = 'pending';
      check.fp = key;
      this.syncCheck();
      check.promise = fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token) + '/photos/check' + (position > 1 ? '?pet=' + position : ''), {
        method: 'POST', mode: 'cors', credentials: 'omit'
      }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }).then(function (body) {
        var current = self.pet(position);
        if (!current || current.check !== check || check.fp !== key) return; /* the photos moved on */
        if (!body || body.status !== 'ready') { check.status = 'none'; self.syncCheck(); return; }
        self.applyCheck(body, position);
        beacon('photo_check', { props: { level: check.level, seen: check.seen, pet: position, praise: check.praise ? 1 : 0 } });
      });
    }

    /* What the service says about a photo set, from the check call or a returning draft. */
    applyCheck(body, position) {
      var pet = this.pet(position);
      var check = pet.check;
      var origin = this.apiOrigin();
      check.status = 'ready';
      check.fp = this.photoKey(pet);
      check.level = body.level || '';
      check.seen = body.seen || 0;
      check.photos = body.photos || {};
      check.main = body.main || '';
      check.subject = body.subject || body.main || '';
      check.split = Boolean(body.split);
      check.praise = body.praise || '';
      check.animals = (body.animals || []).map(function (a) {
        return { id: a.id, description: a.description || '', photos: a.photos || [], cropUrl: a.crop_url ? origin + a.crop_url : '' };
      });
      this.renderPhotos();
      this.syncCheck();
    }

    /* The note under a pet's photos: which one is theirs, or why there will be no sketch. */
    checkHtml(pet) {
      var d = this.dataset;
      var check = pet.check;
      var name = this.petLabel(pet);
      /* Back here from the sketch for better photos: say what will happen, until photos change. */
      if (check.redo && check.redo === this.photoKey(pet)) {
        return '<p class="mq-check__busy"><span class="mq-check__mark mq-check__mark--pencil" aria-hidden="true">✎</span>' +
          escapeHtml((d.txtSketchRedoHint || '').replace(/\{name\}/g, name)) + '</p>';
      }
      /* A sketch already made settles it: the photos it came from are not questioned. */
      if (pet.sketch.status === 'ready') return '';
      if (this.checkBusy(pet)) {
        return '<p class="mq-check__busy"><span class="mq-check__dot" aria-hidden="true"></span>' +
          escapeHtml((d.txtCheckBusy || '').replace('{name}', name)) + '</p>';
      }
      if (!this.checkCurrent(pet)) return '';
      if (check.level !== 'block') {
        /* A photo or two without the pet in a set that is fine: said quietly. */
        var skipped = pet.photos.some(function (p) { return p.state === 'ok' && check.photos[p.id] === 'none'; });
        var two = check.level === 'two' && check.animals.length >= 2 && !check.split;
        /* The reward for uploading: one warm line about what we see on the pet. */
        var praise = !two && check.praise ? '<p class="mq-check__praise"><span class="mq-check__mark mq-check__mark--praise" aria-hidden="true">✓</span>' + escapeHtml(check.praise) + '</p>' : '';
        if (skipped && !two) return praise + '<p class="mq-check__busy"><span class="mq-check__mark" aria-hidden="true">!</span>' + escapeHtml(d.txtCheckSkipped || '') + '</p>';
        if (praise) return praise;
      }
      if (check.level === 'block') {
        var missing = Math.max(1, this.minPhotos - check.seen);
        var text = check.seen ? (d.txtCheckBlock || '') : (d.txtCheckBlockZero || '');
        return '<p class="mq-check__warn">' + escapeHtml(text.replace(/\{name\}/g, name).replace('{n}', check.seen).replace('{k}', missing)) + '</p>';
      }
      if (check.level === 'two' && check.animals.length >= 2 && !check.split) {
        var self = this;
        var picks = check.animals.slice(0, 2).map(function (a) {
          return '<button type="button" class="mq-check__animal' + (a.id === check.subject ? ' is-picked' : '') + '" data-mq-check-pick="' + escapeHtml(a.id) + '">' +
            (a.cropUrl ? '<img src="' + a.cropUrl + '" alt="">' : '<span class="mq-check__desc">' + escapeHtml(a.description) + '</span>') +
            '<span>' + escapeHtml(d.txtCheckOnly || '') + '</span></button>';
        });
        return '<p class="mq-check__h">' + escapeHtml((d.txtCheckTwo || '').replace('{name}', name)) + '</p>' +
          '<div class="mq-check__pick">' + picks.join('') + '</div>' +
          (self.numPets < MAX_PETS ? '<button type="button" class="mq-check__both" data-mq-check-both>' + escapeHtml(d.txtCheckBoth || '') + '</button>' : '');
      }
      return '';
    }

    syncCheck() {
      var self = this;
      var single = this.querySelector('[data-mq-step="photos"] [data-mq-single-only] [data-mq-check-note]');
      var paint = function (el, pet) {
        if (!el) return;
        var html = self.checkHtml(pet);
        if (el.dataset.html !== html) { el.innerHTML = html; el.dataset.html = html; }
        el.hidden = !html;
      };
      if (!this.multi()) { paint(single, this.pet(1)); return; }
      if (single) single.hidden = true;
      this.petList().forEach(function (pet) {
        paint(self.querySelector('[data-mq-pet-cards] [data-mq-pet="' + pet.position + '"] [data-mq-check-note]'), pet);
      });
    }

    /* "Only this one": the sketch is of that animal. */
    pickSubject(position, id) {
      var pet = this.pet(position);
      if (!this.checkCurrent(pet) || !pet.check.animals.some(function (a) { return a.id === id; })) return;
      pet.check.subject = id;
      this.syncCheck();
      if (!this.state.token) return;
      fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token) + '/pets/' + position, {
        method: 'PATCH', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sketch_subject: id })
      }).catch(function () { /* the sketch then follows the animal seen most */ });
    }

    /* "Both": the service copies the photos to a new pet; here that pet appears with its
       card, and the name field waits for its name, as with any added pet. */
    splitPet(position) {
      var self = this;
      var pet = this.pet(position);
      var ids = pet.check.animals.slice(0, 2).map(function (a) { return a.id; });
      if (!this.checkCurrent(pet) || ids.length < 2 || this.numPets >= MAX_PETS || !this.state.token || this.splitting) return;
      var keep = pet.check.subject === ids[1] ? ids[1] : ids[0];
      var other = keep === ids[0] ? ids[1] : ids[0];
      this.splitting = true;
      fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token) + '/pets/' + position + '/split', {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep: keep, new: other })
      }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }).then(function (draft) {
        self.splitting = false;
        if (!draft) { self.showError(self.dataset.errCart || ''); return; }
        var origin = self.apiOrigin();
        var count = Math.max(1, Math.min(MAX_PETS, draft.num_pets || 1));
        var row = (draft.pets || []).find(function (r) { return r.position === count; });
        pet.check.subject = keep;
        pet.check.split = true;
        /* One more card, named next in the field: "Ian" becomes "Ian en ". */
        var text = (self.state.nameText || '').trim();
        self.state.unsplit = false;
        if (text) self.setNameText(text + ' ' + (self.dataset.txtAnd || '&') + ' ');
        self.petVia = 'split';
        self.setPetCount(count);
        self.petVia = '';
        self.nameCount = self.numPets;
        self.countFor = count;
        self.countPromise = Promise.resolve(self.state.token);
        if (row) {
          var twin = self.pet(count);
          twin.photos = (row.photos || []).map(function (p) {
            return { id: String(p.id), name: p.name || '', url: p.url ? origin + p.url : '', state: 'ok', file: null };
          });
          if (row.check && row.check.status === 'ready') self.applyCheck(row.check, count);
        }
        self.renderPhotos();
        self.syncCheck();
        self.render();
        var input = self.querySelector('[data-mq-first-name]');
        if (input) {
          input.focus();
          try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) { /* not a text input */ }
        }
      });
    }

    /* A sketch is due when there is none yet, or when the photos are not the ones the
       current sketch was drawn from and a sketch is still to be had (three per pet). */
    sketchDue(pet) {
      var sk = pet.sketch;
      if (sk.status === 'none') return true;
      if (sk.status !== 'ready' && sk.status !== 'failed') return false;
      return sk.attemptsLeft > 0 && Boolean(sk.forKey) && sk.forKey !== this.photoKey(pet);
    }

    /* Which pet a sketch frame belongs to: the carousel's frame carries it, the single one is pet 1. */
    framePet(el) {
      var fig = el.closest('[data-mq-pet]');
      return fig ? Math.max(1, Math.min(this.numPets, Number(fig.dataset.mqPet))) : 1;
    }

    /* The redo icon says nothing on its own, so it asks first: what a sketch is, and that
       better photos are what changes it. */
    askRedo(position) {
      var dialog = this.querySelector('[data-mq-redo-dialog]');
      var pet = this.pet(position);
      if (!dialog || typeof dialog.showModal !== 'function') { this.redoPhotos(position); return; }
      var text = dialog.querySelector('[data-mq-redo-text]');
      if (text) text.textContent = (text.dataset.template || '').replace(/\{name\}/g, this.petLabel(pet));
      this.pendingRedo = position;
      openDialog(dialog);
    }

    /* Back to the photos with a hint that says what happens next. */
    redoPhotos(position) {
      position = position || 1;
      var pet = this.pet(position);
      pet.check.redo = this.photoKey(pet);
      beacon('sketch_redo', { props: { pet: position } });
      this.goToPhotos(position);
    }

    sketchUrl(position) {
      return this.api + '/drafts/' + encodeURIComponent(this.state.token) + '/sketch' + (position > 1 ? '?pet=' + position : '');
    }

    requestSketch(position) {
      var self = this;
      position = position || 1;
      var sk = this.pet(position).sketch;
      if (!this.state.token || sk.status === 'pending') return;
      var had = sk.url;
      sk.again = Boolean(sk.url);
      sk.forKey = this.photoKey(this.pet(position));
      sk.status = 'pending';
      sk.since = Date.now();
      this.renderSketch();
      track('SketchRequested', {});
      fetch(this.sketchUrl(position), {
        method: 'POST', mode: 'cors', credentials: 'omit'
      }).then(function (r) {
        /* The same photos as the sketch they have: that sketch stays. */
        if (r.status === 429 && had) { sk.status = 'ready'; sk.again = false; self.renderSketch(); return null; }
        /* 429: no attempts left, 503: switched off or over the daily budget. Neither
           is worth retrying from here; 422 means the photos were not in yet. */
        if (r.status === 429 || r.status === 503) return { status: 'failed', url: null, attempts_left: 0 };
        if (!r.ok) return { status: 'failed', url: null, attempts_left: sk.attemptsLeft };
        return r.json();
      }).catch(function () {
        return { status: 'failed', url: null, attempts_left: sk.attemptsLeft };
      }).then(function (body) {
        if (!body || self.pet(position).sketch !== sk) return;
        self.applySketch(body, position);
      });
    }

    pollSketch(position) {
      var self = this;
      var sk = this.pet(position).sketch;
      clearTimeout(sk.timer);
      sk.timer = setTimeout(function () {
        if (!self.state.token || self.pet(position).sketch !== sk) return;
        if (Date.now() - sk.since > SKETCH_GIVE_UP_MS) {
          self.applySketch({ status: 'failed', url: sk.url || null, attempts_left: sk.attemptsLeft }, position);
          return;
        }
        fetch(self.sketchUrl(position), {
          mode: 'cors', credentials: 'omit'
        }).then(function (r) {
          return r.ok ? r.json() : null;
        }).catch(function () { return null; }).then(function (body) {
          if (self.pet(position).sketch !== sk) return;
          if (body) self.applySketch(body, position);
          else self.pollSketch(position);
        });
      }, SKETCH_POLL_MS);
    }

    applySketch(body, position) {
      var self = this;
      position = position || 1;
      var sk = this.pet(position).sketch;
      var url = body.url ? this.apiOrigin() + body.url : '';
      sk.attemptsLeft = typeof body.attempts_left === 'number' ? body.attempts_left : sk.attemptsLeft;
      if (body.figure_url) {
        sk.figureUrl = this.apiOrigin() + body.figure_url;
        if (position === 1) this.applyFigure();
      }
      if (body.status === 'pending') {
        sk.status = 'pending';
        this.renderSketch();
        this.pollSketch(position);
        return;
      }
      /* Too few photos with the animal on them: no sketch, and the attempt is kept. */
      if (body.status === 'blocked') {
        sk.status = 'blocked';
        this.renderSketch();
        return;
      }
      if (body.status === 'ready' && url) {
        /* Only reveal once the image has loaded, so the frame never shows half a picture. */
        var img = new Image();
        img.onload = function () {
          sk.status = 'ready';
          if (position === 1) self.fetchFigure();
          else self.fetchFigureFor(position);
          sk.url = url;
          self.renderSketch();
          track('SketchReady', { seconds: Math.round((Date.now() - sk.since) / 1000) });
          beacon('sketch_ready', { props: { seconds: Math.round((Date.now() - sk.since) / 1000), pet: position } });
        };
        img.onerror = function () {
          sk.status = 'failed';
          self.renderSketch();
        };
        img.src = url;
        return;
      }
      sk.status = 'failed';
      if (url) sk.url = url;
      this.renderSketch();
      track('SketchFailed', {});
      beacon('sketch_failed');
    }

    /* Their own pet in the true-scale row next to the mug, instead of the stock figurine.
       The cut-out is made after the sketch, so the size step asks for it a few times;
       until it is there, or if it never comes, the stock figurine stays: no loader. */
    applyFigure() {
      var url = this.sketch.figureUrl;
      if (this.multi()) this.syncSetPreview();
      var imgs = this.querySelectorAll('[data-mq-scale-pick] img');
      if (!imgs.length) return;
      if (!url) {
        imgs.forEach(function (img) { if (img.dataset.stock) img.src = img.dataset.stock; });
        return;
      }
      if (this.figureShown === url) return;
      var self = this;
      var probe = new Image();
      probe.onerror = function () { self.sketch.figureFailed = true; };
      probe.onload = function () {
        if (self.sketch.figureUrl !== url) return;
        self.figureShown = url;
        self.sketch.figureShown = true;
        if (self.multi()) self.syncSetPreview();
        imgs.forEach(function (img) {
          if (!img.dataset.stock) img.dataset.stock = img.getAttribute('src');
          img.src = url;
          img.classList.add('is-theirs');
        });
      };
      probe.src = url;
    }

    fetchFigure() {
      var self = this;
      var sk = this.sketch;
      if (sk.figureUrl || sk.figureFailed || sk.status !== 'ready' || this.figureTries >= FIGURE_TRIES || this.figureTimer || !this.state.token) return;
      this.figureTimer = setTimeout(function () {
        self.figureTimer = null;
        self.figureTries = (self.figureTries || 0) + 1;
        fetch(self.sketchUrl(1), { mode: 'cors', credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (body) {
            if (body && body.figure_url && self.sketch === sk) {
              sk.figureUrl = self.apiOrigin() + body.figure_url;
              self.applyFigure();
            } else if (body && body.figure_status === 'failed' && self.sketch === sk) {
              sk.figureFailed = true;  /* no loader for something that will not come */
            } else {
              self.fetchFigure();
            }
          });
      }, this.figureTries ? 4000 : 0);
    }

    renderSketch() {
      var sk = this.sketch;
      var all = this.petList().map(function (p) { return p.sketch.status; });
      /* The chip on the pet step: drawing while any sketch renders, ready once all are. */
      var pending = all.indexOf('pending') >= 0;
      var chipReady = !pending && all.indexOf('ready') >= 0;
      var show = function (sel, visible) {
        var el = this.querySelector(sel);
        if (el) el.hidden = !visible;
        return el;
      }.bind(this);

      var chip = show('[data-mq-sketch-chip]', pending || chipReady);
      if (chip) chip.classList.toggle('is-ready', chipReady);
      show('[data-mq-chip-wait]', pending);
      show('[data-mq-chip-ready]', chipReady);
      pending = sk.status === 'pending';

      var figure = this.querySelector('[data-mq-sketch]');
      if (figure) figure.dataset.state = sk.status;
      var hasImage = Boolean(sk.url) && !pending;
      show('[data-mq-sketch-wait]', pending || sk.status === 'none');
      show('[data-mq-sketch-frame]', hasImage);
      show('[data-mq-sketch-fail]', sk.status === 'failed' && !hasImage);
      var heading = this.querySelector('[data-mq-step="sketch"] h2');
      if (heading) {
        if (!heading.dataset.templateFirst) heading.dataset.templateFirst = heading.dataset.template || '';
        heading.dataset.template = (sk.again && this.dataset.txtSketchHAgain) || heading.dataset.templateFirst;
        this.syncNames();
      }
      var tools = this.querySelector('[data-mq-step="sketch"] [data-mq-single-only] .mq-sketch__tools');
      if (tools) tools.hidden = !(sk.status === 'ready' && sk.url);
      var blocked = show('[data-mq-sketch-blocked]', sk.status === 'blocked');
      if (blocked) blocked.querySelector('.mq-sketch__blocked-text').textContent = (this.dataset.txtSketchBlocked || '').replace(/\{name\}/g, this.state.petName || this.dataset.txtNameFallback || '');
      /* The notice explains a sketch; without one it is noise. */
      var notice = this.querySelector('[data-mq-step="sketch"] .mq-sketch-notice');
      if (notice) notice.hidden = sk.status === 'blocked' && !this.multi();
      var img = this.querySelector('[data-mq-sketch-img]');
      if (img && hasImage && img.getAttribute('src') !== sk.url) img.src = sk.url;

      this.renderSketches();
      this.syncWaitLines();
    }

    /* Rotating lines while the sketch renders, only while someone is looking at them. */
    syncWaitLines() {
      var line = this.querySelector('[data-mq-sketch-line]');
      var active = STEPS[this.stepIndex] === 'sketch' && this.sketch.status === 'pending' && !this.multi();
      if (!line) return;
      if (!active) { clearInterval(this.waitTimer); this.waitTimer = null; return; }
      if (this.waitTimer) return;
      var lines = (this.dataset.txtWait || '').split('|').filter(Boolean);
      var name = this.state.petName || this.dataset.txtNameFallback || '';
      var i = 0;
      var paint = function () {
        line.textContent = (lines[i % lines.length] || '').replace('{name}', name);
      };
      paint();
      if (lines.length < 2) return;
      this.waitTimer = setInterval(function () {
        line.classList.add('is-fading');
        setTimeout(function () { i += 1; paint(); line.classList.remove('is-fading'); }, 300);
      }, WAIT_LINE_MS);
    }

    /* ---------------- state ---------------- */

    persist() {
      try {
        /* While the studio is closed the step on screen means nothing; keep the saved one. */
        var step = this.classList.contains('is-open') ? STEPS[this.stepIndex] : (this.savedStep || STEPS[this.stepIndex]);
        var copy = Object.assign({}, this.state, { step: step });
        if (this.multi()) {
          copy.numPets = this.numPets;
          copy.extra = this.extra.slice(0, this.numPets - 1).map(function (p) {
            return { name: p.name, species: p.species, speciesOther: p.speciesOther, breed: p.breed, note: p.note, size: p.size };
          });
        }
        localStorage.setItem(STORE_KEY, JSON.stringify(copy));
      } catch (e) { /* private mode */ }
    }

    /* A size the shop no longer sells can still sit in this browser or on the draft (12 and
       18 cm, until 2026-10-11). It has no card and no price: the shop's own choice takes
       its place, and a pet with a size of its own follows the order again. */
    dropUnsoldSizes() {
      if (this.state.size && !this.sizes[this.state.size]) {
        this.state.size = this.shopSize || Object.keys(this.sizes)[0] || '';
        this.sizeIsDefault = true;
      }
      this.extra.forEach(function (pet) { if (pet.size && !this.sizes[pet.size]) pet.size = ''; }, this);
    }

    restore() {
      /* Typed details, the size, the token and the step come back from localStorage.
         Photos and the sketch live on the draft and are fetched by resume(). */
      try {
        var saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
        if (!saved) return;
        ['token', 'petName', 'species', 'speciesOther', 'breed', 'note', 'email', 'phone', 'size', 'nameText', 'unsplit', 'sizeMode'].forEach(function (k) {
          if (saved[k]) this.state[k] = saved[k];
        }, this);
        this.savedStep = saved.step || '';
        this.numPets = Math.max(1, Math.min(MAX_PETS, saved.numPets | 0 || 1));
        (saved.extra || []).slice(0, MAX_PETS - 1).forEach(function (fields, i) {
          ['name', 'species', 'speciesOther', 'breed', 'note', 'size'].forEach(function (k) { if (fields && fields[k]) this.extra[i][k] = String(fields[k]); }, this);
        }, this);
        if (saved.size) this.sizeIsDefault = false;
        this.dropUnsoldSizes();
        if (this.state.size) {
          var size = this.querySelector('[name="mq-size"][value="' + this.state.size + '"]');
          if (size) size.checked = true;
        }
        var map = {
          petName: '[data-mq-pet-name]', breed: '[data-mq-breed]', note: '[data-mq-note]',
          email: '[data-mq-email]', phone: '[data-mq-phone]'
        };
        Object.keys(map).forEach(function (k) {
          var el = this.querySelector(map[k]);
          if (el && this.state[k]) el.value = this.state[k];
        }, this);
      } catch (e) { /* private mode */ }
    }

    /* A follow-up e-mail links to ?mq_resume=<token>. That draft becomes this browser's
       draft, even on another device, and the studio opens where they left off. The token
       is taken out of the address bar straight away. */
    adoptResumeLink() {
      try {
        var params = new URLSearchParams(location.search);
        var token = params.get('mq_resume');
        if (!token) return;
        localStorage.setItem(STORE_KEY, JSON.stringify({ token: token }));
        this.openFromLink = true;
        params.delete('mq_resume');
        var query = params.toString();
        history.replaceState(null, '', location.pathname + (query ? '?' + query : '') + location.hash);
      } catch (e) { /* private mode: the link still opens the shop */ }
    }

    /* Fetch the draft behind a saved token. A draft that was ordered (409) or is gone
       (404) is forgotten; anything else restores the photos and the sketch. */
    resume() {
      var self = this;
      if (!this.state.token) return;
      fetch(this.api + '/drafts/' + encodeURIComponent(this.state.token), { mode: 'cors', credentials: 'omit' })
        .then(function (r) {
          if (r.status === 404 || r.status === 409) { self.forget(r.status === 409); return null; }
          return r.ok ? r.json() : null;
        })
        .catch(function () { return null; })
        .then(function (draft) {
          if (!draft) return;
          var origin = self.apiOrigin();
          /* Everything the visitor typed lives on the draft too; fill in what this browser lacks. */
          var fields = { petName: 'pet_name', species: 'species', speciesOther: 'species_other', breed: 'breed', note: 'note', email: 'email', size: 'size_key' };
          Object.keys(fields).forEach(function (key) {
            if (!self.state[key] && draft[fields[key]]) self.state[key] = draft[fields[key]];
          });
          /* The preselected size is the shop's suggestion, not their choice: the draft wins. */
          if (draft.size_key && (self.openFromLink || self.sizeIsDefault)) self.state.size = draft.size_key;
          self.dropUnsoldSizes();
          [['[data-mq-pet-name]', 'petName'], ['[data-mq-breed]', 'breed'], ['[data-mq-note]', 'note'], ['[data-mq-email]', 'email']].forEach(function (pair) {
            var el = self.querySelector(pair[0]);
            if (el && !el.value && self.state[pair[1]]) el.value = self.state[pair[1]];
          });
          if (self.state.size) {
            var radio = self.querySelector('[name="mq-size"][value="' + self.state.size + '"]');
            if (radio) radio.checked = true;
          }
          /* Where they left off. This browser's last screen and the draft's furthest step
             can disagree: someone who walked back to the photos before closing the tab is
             still further along than that. Take whichever reaches furthest, so coming back
             never hands someone step one of a form they had already finished. */
          if (draft.resume_step && STEPS.indexOf(draft.resume_step) > STEPS.indexOf(self.savedStep)) {
            self.savedStep = draft.resume_step;
          }
          var toPhotos = function (list) {
            return (list || []).map(function (p) {
              return { id: String(p.id), name: p.name || '', url: p.url ? origin + p.url : '', state: 'ok', file: null };
            });
          };
          self.photos = toPhotos(draft.photos);
          /* Several pets: the draft knows how many. A higher count in this browser that never
             reached the draft (offline) stays, and is sent with the next upload. */
          var drafted = Math.max(1, Math.min(MAX_PETS, draft.num_pets || 1));
          self.countFor = drafted;
          self.countPromise = Promise.resolve(self.state.token);
          if (drafted > self.numPets || self.openFromLink) self.numPets = drafted;
          (draft.pets || []).forEach(function (row) {
            if (row.position < 2 || row.position > MAX_PETS) return;
            var pet = self.extra[row.position - 2];
            [['name', 'pet_name'], ['species', 'species'], ['speciesOther', 'species_other'], ['breed', 'breed'], ['note', 'note']].forEach(function (pair) {
              if (!pet[pair[0]] && row[pair[1]]) pet[pair[0]] = row[pair[1]];
            });
            /* A pet with a size of its own means sizes were chosen per pet. */
            if (row.size_key && self.sizes[row.size_key] && !pet.size) { pet.size = row.size_key; self.state.sizeMode = 'each'; }
            pet.photos = toPhotos(row.photos);
          });
          self.buildPets();
          self.syncNameField();
          self.renderPhotos();
          self.petList().forEach(function (pet) {
            var sketch = pet.position === 1 ? draft.sketch : ((draft.pets || []).find(function (r) { return r.position === pet.position; }) || {}).sketch;
            if (sketch && sketch.status !== 'none') {
              pet.sketch.since = Date.now();
              /* A stale sketch has no key: the next Verder draws again. */
              pet.sketch.forKey = sketch.stale ? '' : self.photoKey(pet);
              self.applySketch(sketch, pet.position);
            } else {
              pet.sketch.attemptsLeft = sketch ? sketch.attempts_left : 1;
            }
            var row = pet.position === 1 ? draft : ((draft.pets || []).find(function (r) { return r.position === pet.position; }) || {});
            if (row.check && row.check.status === 'ready') self.applyCheck(row.check, pet.position);
          });
          var hasPhotos = self.petList().some(function (p) { return p.photos.length; });
          if (hasPhotos) {
            self.resumable = true;
            labelButtons(self);
            track('StudioResume', { step: self.savedStep || '' });
            beacon('resume', { props: { step: self.savedStep || '', via: self.openFromLink ? 'email' : 'return' } });
          }
          self.persist();
          self.render();
          if (self.openFromLink && self.resumable) {
            self.openFromLink = false;
            self.open([], 'email');
          }
        });
    }

    /* The furthest step up to the saved one whose earlier steps are all complete. */
    resumeStep() {
      if (!this.resumable) return 0;
      var target = STEPS.indexOf(this.savedStep);
      var saved = this.stepIndex;
      var reach = 0;
      for (var i = 0; i < Math.max(target, 0); i++) {
        this.stepIndex = i;
        if (this.validate()) break;
        reach = i + 1;
      }
      this.stepIndex = saved;
      return reach;
    }

    /* Drop the draft. After an order everything goes; the e-mail address stays. */
    forget(ordered) {
      clearTimeout(this.sketch.timer);
      var email = this.state.email;
      this.state.token = '';
      this.countFor = 0;
      this.countPromise = null;
      this.photos = [];
      this.resumable = false;
      this.uploadTracked = false;
      this.savedStep = '';
      this.sketch = emptySketch();
      this.figureShown = '';
      this.figureTries = 0;
      this.applyFigure();
      this.extra.forEach(function (p, i) {
        clearTimeout(p.sketch.timer);
        if (ordered) this.extra[i] = extraPet(p.position);
        else { p.photos = []; p.sketch = emptySketch(); }
      }, this);
      if (ordered) {
        ['petName', 'species', 'speciesOther', 'breed', 'note', 'phone', 'nameText'].forEach(function (k) { this.state[k] = ''; }, this);
        this.state.unsplit = false;
        this.state.sizeMode = 'same';
        this.state.email = email;
        this.numPets = 1;
      }
      this.persist();
      this.buildPets();
      this.syncNameField();
      this.renderPhotos();
      this.renderSketch();
      labelButtons(this);
    }

    startOver() {
      var self = this;
      this.askToRestart(function () { self.restartNow(); });
    }

    /* Starting over throws the draft away: the photos, the sketch, the details. It asks
       first, the way removing a pet asks. Without a <dialog> it goes ahead, as before. */
    askToRestart(after) {
      var dialog = this.querySelector('[data-mq-restart-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') { after(); return; }
      this.pendingRestart = after;
      /* A pet with photos and room for one more: offer to add it to this order first. */
      var offer = dialog.querySelector('[data-mq-restart-offer]');
      var plain = dialog.querySelector('[data-mq-restart-plain]');
      var canAdd = Boolean(offer) && this.numPets < MAX_PETS && this.hasPhotos();
      if (offer) offer.hidden = !canAdd;
      if (plain) plain.hidden = canAdd;
      if (canAdd) {
        var text = offer.querySelector('[data-mq-restart-offer-text]');
        var name = this.displayName() || this.dataset.txtNameFallback || '';
        if (text) text.textContent = (text.dataset.template || '').replace('{name}', name);
      }
      openDialog(dialog);
    }

    restartNow() {
      track('StudioRestart', {});
      beacon('restart');
      /* The figurine of the draft being thrown away goes with it. */
      this.clearCart();
      this.forget(true);
      this.querySelectorAll('[data-mq-pet-name], [data-mq-breed], [data-mq-note]').forEach(function (el) { el.value = ''; });
      this.stepIndex = 0;
      this.showError('');
      this.render();
    }

    onChange(event) {
      var t = event.target;
      if (t.matches('[data-mq-first-name]')) {
        if (!t.value.trim()) this.state.unsplit = false;
        this.state.nameText = t.value;
        this.applyNames();
      }
      if (t.matches('[data-mq-pet-name]')) this.state.petName = t.value.trim();
      if (t.matches('[data-mq-breed]')) this.state.breed = t.value.trim();
      if (t.matches('[data-mq-note]')) this.state.note = t.value.trim();
      if (t.matches('[data-mq-email]')) this.state.email = t.value.trim();
      if (t.matches('[data-mq-phone]')) this.state.phone = t.value.trim();
      if (t.matches('[name="mq-size"]')) this.state.size = t.value;
      /* The cart holds deposit or figurine variants, so it follows the choice at once. */
      if (t.matches('[name="mq-pay"]')) {
        this.state.payFull = t.value === 'full';
        this.ensureCart();
      }
      var card = t.closest('[data-mq-pet]');
      if (card && t.matches('[data-mq-card-name], [data-mq-card-breed], [data-mq-card-note]')) {
        var pet = this.pet(Number(card.dataset.mqPet));
        if (t.matches('[data-mq-card-name]')) pet.name = t.value.trim();
        if (t.matches('[data-mq-card-breed]')) pet.breed = t.value.trim();
        if (t.matches('[data-mq-card-note]')) pet.note = t.value.trim();
        this.syncPets();
      }
      this.persist();
      this.syncNames();
      this.syncSummary();
      this.syncNext();
    }

    /* Sentences that carry the pet's name update as it is typed. */
    syncNames() {
      var name = this.displayName();
      this.querySelectorAll('[data-template]').forEach(function (el) {
        var tpl = el.dataset.template || '';
        if (!tpl) return;
        el.textContent = tpl.replace('{name}', name || el.dataset.fallback || '');
      });
    }

    /* ---------------- navigation ---------------- */

    validate() {
      var step = STEPS[this.stepIndex];
      if (step === 'photos' && this.multi()) return this.petsProblem('photos');
      if (step === 'pet' && this.multi()) {
        var details = this.petsProblem('details');
        if (details) return details;
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.state.email)) return this.dataset.errEmail || 'Enter a valid email address.';
      }
      if (step === 'pet' && !this.multi()) {
        if (!this.state.petName) return this.dataset.errName || 'Tell us the name.';
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.state.email)) return this.dataset.errEmail || 'Enter a valid email address.';
      }
      if (step === 'photos') {
        if (!this.state.petName) return this.dataset.errName || 'Tell us the name.';
        var ok = this.photos.filter(function (p) { return p.state === 'ok'; }).length;
        var failed = this.photos.some(function (p) { return p.state === 'fail'; });
        if (failed) return this.dataset.errUpload || 'One photo did not arrive.';
        if (ok < this.minPhotos) return this.dataset.errPhotos || 'Add at least three photos.';
      }
      if (step === 'size' && !this.state.size) return this.dataset.errSize || 'Choose a size.';
      if (step === 'size' && this.multi() && this.petSizes().some(function (size) { return !size || !size.variant; })) {
        return this.dataset.errSize || 'Choose a size.';
      }
      return '';
    }

    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.textContent = msg;
      this.errorBox.hidden = !msg;
    }

    next() {
      var self = this;
      var problem = this.validate();
      if (problem && STEPS[this.stepIndex] === 'photos' && this.multi()) {
        this.showBlocked = true;
        this.syncPets();
        var card = this.firstUnreadyCard();
        if (card) { try { card.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { card.scrollIntoView(); } }
        return;
      }
      if (problem) { this.showError(problem); return; }
      this.showError('');
      var leaving = STEPS[this.stepIndex];
      /* Asked on the way to the sketch, not on the way out of the photos: by then the look
         at the photos is long in, so nobody waits on the photo step for it. */
      if (leaving === 'pet' && !this.confirmNoSketch(function () { self.next(); })) return;
      var saved = this.save();
      if (leaving === 'pet') track('Lead', this.multi() ? { num_pets: this.numPets } : {});
      var done = { photos: 'photos_done', pet: 'lead', sketch: 'sketch_done', size: 'size_done' }[leaving];
      var props = leaving === 'size' ? { size: this.state.size } : null;
      if (props && this.multi()) props.num_pets = this.numPets;
      if (done) beacon(done, props ? { props: props } : (this.multi() ? { props: { num_pets: this.numPets } } : null));
      if (leaving === 'photos' || leaving === 'pet') {
        if (!this.multi()) {
          if (this.sketchDue(this.pet(1))) this.requestSketch();
        } else {
          /* The sketches use each pet's name and kind, so they wait until those are saved. */
          Promise.resolve(saved).then(function () {
            self.petList().forEach(function (pet) { if (self.sketchDue(pet)) self.requestSketch(pet.position); });
          });
        }
      }
      if (this.stepIndex >= STEPS.length - 1) return;
      this.stepIndex += 1;
      this.render();
      if (STEPS[this.stepIndex] === 'sketch') track('SketchViewed', { status: this.sketch.status });
      if (leaving === 'sketch') track('SketchDone', {});
      if (leaving === 'size') {
        var chosen = this.sizes[this.state.size];
        track('SizeChosen', { size: this.state.size, value: this.orderCents(chosen) / 100, currency: this.dataset.currency || 'EUR', num_items: this.numPets });
        /* Shopify sees the figurine the moment the size is picked, not one step later. The
           cart waits for the draft: its token is what the paid-order webhook claims. */
        Promise.resolve(saved).then(function () { self.ensureCart(); }, function () { /* no draft, no cart */ });
      }
      if (STEPS[this.stepIndex] === 'details') track('DetailsViewed', {});
    }

    back() {
      if (this.stepIndex === 0) { this.close(); return; }
      this.stepIndex -= 1;
      this.showError('');
      this.render();
    }

    render() {
      this.steps.forEach(function (el) {
        el.classList.toggle('is-active', el.dataset.mqStep === STEPS[this.stepIndex]);
      }, this);
      if (this.bar) {
        Array.from(this.bar.children).forEach(function (seg, i) {
          seg.classList.toggle('is-done', i <= this.stepIndex);
        }, this);
      }
      this.syncNames();
      this.syncSummary();
      this.syncNext();
      this.syncWaitLines();
      if (STEPS[this.stepIndex] === 'size' || STEPS[this.stepIndex] === 'sketch') {
        this.fetchFigure();
        if (this.multi()) this.extra.slice(0, this.numPets - 1).forEach(function (pet) { this.fetchFigureFor(pet.position); }, this);
      }
      if (this.multi()) this.syncSetPreview();
      if (this.classList.contains('is-open') && this.lastStepView !== STEPS[this.stepIndex]) {
        this.lastStepView = STEPS[this.stepIndex];
        beacon('step_view', { step: this.lastStepView });
      }
      this.syncRestart();
      if (this.classList.contains('is-open')) this.persist();
      this.scrollTop = 0;
    }

    syncNext() {
      var step = STEPS[this.stepIndex];
      var btn = this.querySelector('[data-mq-step="' + step + '"] [data-mq-next], [data-mq-step="' + step + '"] [data-mq-add]');
      if (!btn) return;
      var sending = step === 'photos' && this.petList().some(function (pet) {
        return pet.photos.some(function (p) { return p.state === 'sending'; });
      });
      /* Several pets: "Verder" stays tappable and says what is missing, on the right card. */
      btn.disabled = sending || (step === 'photos' && this.multi() ? false : Boolean(this.validate()));
    }

    /* ---------------- summary + cart ---------------- */

    syncSummary() {
      var size = this.sizes[this.state.size];
      var set = function (sel, value) {
        var el = this.querySelector(sel);
        if (el) el.textContent = value || '-';
      }.bind(this);
      set('[data-mq-sum-pet]', this.state.petName);
      set('[data-mq-sum-photos]', String(this.photos.filter(function (p) { return p.state === 'ok'; }).length));
      set('[data-mq-sum-stance]', this.dataset.stanceLabel || this.state.stance);
      set('[data-mq-sum-size]', size ? size.label : '');
      /* Their own photo on the summary, never the sketch: the last step is about their pet. */
      var photo = this.querySelector('[data-mq-sum-photo]');
      var first = this.photos.find(function (p) { return p.state === 'ok' && p.url; });
      if (photo) {
        var src = first ? first.url : '';
        var img = photo.querySelector('img');
        if (!src && img) img.remove();
        if (src && (!img || img.getAttribute('src') !== src)) {
          if (!img) { img = document.createElement('img'); img.alt = ''; photo.appendChild(img); }
          img.src = src;
        }
      }
      set('[data-mq-sum-total]', size ? money(size.price, this.dataset.currency) : '');
      var total = this.orderCents(size);
      var addBtn = this.querySelector('[data-mq-add]');
      var split = this.payDeposit() ? this.split() : null;
      if (addBtn) {
        /* Both labels always sit in the button, stacked; switching only hides one. The button
           never resizes its text, which Safari on iOS clipped at the first label's width. */
        var offer = this.depositMode() ? this.split() : null;
        var full = addBtn.querySelector('[data-mq-add-full]');
        var now = addBtn.querySelector('[data-mq-add-now]');
        full.textContent = (this.dataset.txtAdd || 'Order') + (size ? ' · ' + money(total, this.dataset.currency) : '');
        now.textContent = offer && this.dataset.txtAddNow ? this.dataset.txtAddNow.replace('{amount}', money(offer.now, this.dataset.currency)) : '';
        var showNow = Boolean(split && this.dataset.txtAddNow);
        full.classList.toggle('is-off', showNow);
        now.classList.toggle('is-off', !showNow);
      }
      this.syncDue();
      if (this.multi()) this.syncOrderList(size);

      /* The size step: the chosen figurine stands out in the scale row, and the button
         names the size and its price. */
      var chosen = this.state.size;
      this.querySelectorAll('[data-mq-scale-pick]').forEach(function (el) {
        el.classList.toggle('is-selected', el.dataset.mqScalePick === chosen);
      });
      var sizeNext = this.querySelector('[data-mq-size-next]');
      if (sizeNext && !sizeNext.dataset.plain) sizeNext.dataset.plain = sizeNext.textContent;
      if (sizeNext && this.multi() && this.state.sizeMode === 'each') {
        sizeNext.textContent = sizeNext.dataset.plain;
      } else if (sizeNext && size && sizeNext.dataset.sizeTemplate) {
        sizeNext.textContent = sizeNext.dataset.sizeTemplate
          .replace('{size}', size.label)
          .replace('{price}', money(total, this.dataset.currency));
      }
    }

    /* Several pets: the size applies to all, with the price lines under the sizes and a
       row per pet on the last step. */
    syncOrderList(size) {
      var self = this;
      var all = this.querySelector('[data-mq-all-sizes]');
      if (all) all.textContent = (this.dataset.txtAllSizes || '').replace('{n}', this.numPets);
      if (all) all.hidden = this.state.sizeMode === 'each';
      var view = this.querySelector('[data-mq-price-view]');
      if (view) view.innerHTML = size ? this.totalHtml(true) : '';
      this.syncSizeMode();
      var list = this.querySelector('[data-mq-order-list]');
      if (!list) return;
      var currency = this.dataset.currency;
      var rows = this.petList().map(function (pet) {
        var first = pet.photos.find(function (p) { return p.state === 'ok' && p.url; });
        var own = self.sizes[self.sizeKeyOf(pet)];
        return '<li class="mq-setorder__pet">' +
          '<span class="mq-setorder__photo">' + (first ? '<img src="' + escapeHtml(first.url) + '" alt="">' : '') + '</span>' +
          '<span class="mq-setorder__main"><b>' + escapeHtml(self.petLabel(pet)) + '</b><small>' + escapeHtml(own ? own.label : '-') + '</small></span>' +
          '<span class="mq-setorder__price">' + (own ? money(own.price, currency) : '') + '</span></li>';
      }).join('');
      var html = '<div class="mq-setorder"><ul class="mq-setorder__pets">' + rows + '</ul>' + (size ? this.totalHtml() : '') + '</div>';
      if (list.dataset.html !== html) { list.innerHTML = html; list.dataset.html = html; }
    }

    /* The hidden properties the backend reads, for one pet. */
    petProps(pet, size) {
      var props = {};
      props[this.dataset.propName || 'Name'] = pet.name;
      props._studio = this.state.token;
      props._pet = String(pet.position);
      props._pet_name = pet.name;
      if (pet.species) props._species = pet.species;
      if (pet.speciesOther) props._species_other = pet.speciesOther;
      if (pet.breed) props._breed = pet.breed;
      if (pet.note) props._note = pet.note;
      props._pose = this.state.stance;
      props._size = this.sizeKeyOf(pet);
      props._email = this.state.email;
      if (this.state.phone) props._phone = this.state.phone;
      props._locale = this.dataset.locale || 'en';
      if (this.dataset.context) props._context = this.dataset.context;
      return props;
    }

    /* ---------------- cart ----------------

       The cart is written as soon as a size is picked, so Shopify and the ad platforms see
       the figurine at the moment the price is accepted rather than one step later.

       Only an add counts as an addition. A swapped size is another variant and can only be
       added, so that counts again; a name or a note typed earlier is a properties change,
       edited where it stands with change.js. Picking a size, wandering back and landing on
       the same one writes nothing at all, so one decision is counted once.

       Every write goes through one chain, so two of them can never interleave. */

    cartRoot() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    }

    cartGet(path) {
      return fetch(this.cartRoot() + path, { headers: { 'Accept': 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });
    }

    cartPost(path, body) {
      var options = { method: 'POST', headers: { 'Accept': 'application/json' } };
      if (body) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
      return fetch(this.cartRoot() + path, options).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json().catch(function () { return null; });
      });
    }

    /* The hidden fields of a single figurine. Everything is typed before the size step, so
       a line written there is already complete. */
    singleProps() {
      var props = {};
      props[this.dataset.propName || 'Name'] = this.state.petName;
      /* No visible size line: the variant already shows it in cart and checkout. No visible
         "Pose" line either, one pose at launch. The hidden _size and _pose below tell the
         backend the size and the stance to model. */
      props._studio = this.state.token;
      props._pet_name = this.state.petName;
      if (this.state.species) props._species = this.state.species;
      if (this.state.speciesOther) props._species_other = this.state.speciesOther;
      if (this.state.breed) props._breed = this.state.breed;
      if (this.state.note) props._note = this.state.note;
      props._pose = this.state.stance;
      props._size = this.state.size;
      props._email = this.state.email;
      if (this.state.phone) props._phone = this.state.phone;
      props._locale = this.dataset.locale || 'en';
      if (this.dataset.context) props._context = this.dataset.context;
      return props;
    }

    /* What the cart should hold: one line per pet, each with its own size, and for several
       pets the automatic 20% in Shopify takes care of the rest. Null while the draft or a
       size is still missing, which is what keeps an empty cart empty. */
    intendedItems() {
      var self = this;
      if (!this.state.token) return null;
      /* Deposit checkout: the deposit variant of each size, and on every line a visible
         "rest after approval" so checkout itself says what comes later. */
      var deposit = this.payDeposit();
      var split = deposit ? this.split() : null;
      var restKey = this.dataset.propRest || 'Rest after approval';
      var currency = this.dataset.currency;
      var line = function (size, props, i) {
        if (deposit && split && split.lines[i]) props[restKey] = money(split.lines[i].later, currency);
        return { id: Number(deposit ? size.depositVariant : size.variant), quantity: 1, properties: props };
      };
      if (this.multi()) {
        var sizes = this.petSizes();
        if (!sizes.length || sizes.some(function (size) { return !size || !size.variant; })) return null;
        return this.petList().map(function (pet, i) {
          var own = self.sizes[self.sizeKeyOf(pet)];
          return line(own, self.petProps(pet, own), i);
        });
      }
      var size = this.sizes[this.state.size];
      if (!size || !size.variant) return null;
      return [line(size, this.singleProps(), 0)];
    }

    /* One cart job after another. The chain itself never stays rejected, so a write that
       failed cannot take the next one down with it; callers handle their own failure. */
    queueCart(job) {
      var run = function () { return job(); };
      var next = (this.cartChain || Promise.resolve()).then(run, run);
      this.cartChain = next.catch(function () { return null; });
      return next;
    }

    /* The lines this studio put there, whichever draft they belong to. */
    studioLines(cart) {
      return ((cart && cart.items) || []).filter(function (line) {
        return line && line.properties && line.properties._studio;
      });
    }

    /* The campaign that brought them, kept on the cart so it reaches the order. Ends on the
       cart itself either way, because the order button reads the total off it. */
    writeAttributes() {
      var attributes = utm();
      if (!Object.keys(attributes).length) return this.cartGet('cart.js');
      return this.cartPost('cart/update.js', { attributes: attributes });
    }

    /* Put the cart where the studio is, and hand back the cart Shopify ended up with. */
    syncCart() {
      var self = this;
      var items = this.intendedItems();
      if (!items) return Promise.reject(new Error('cart'));
      return this.cartGet('cart.js').then(function (cart) {
        var lines = (cart && cart.items) || [];
        if (Logic.cartVariantsMatch(lines, items)) {
          var drift = Logic.cartDrift(lines, items);
          if (!drift.length) return cart;
          /* Only the typed details moved: change.js edits a line where it stands, which
             Shopify records as an edit and never as another addition. One line per call,
             as that endpoint asks. */
          return drift.reduce(function (chain, hit) {
            return chain.then(function () {
              return self.cartPost('cart/change.js', {
                line: hit.line, quantity: items[hit.item].quantity || 1, properties: items[hit.item].properties
              });
            });
          }, Promise.resolve())
            .then(function (updated) { return updated || cart; })
            .catch(function () {
              /* change.js would not take the edit. Saving a count is worth nothing next to
                 an order that cannot be placed: write the cart the plain way instead. */
              return self.writeCart(items);
            });
        }
        /* Another size, or nothing in there yet. */
        return self.writeCart(items);
      });
    }

    /* Emptied first, so going back from checkout and ordering again never leaves two
       figurines in the cart. This is the one write that counts as an addition. */
    writeCart(items) {
      var self = this;
      return this.cartPost('cart/clear.js')
        .then(function () { return self.cartPost('cart/add.js', { items: items }); })
        .then(function () { return self.writeAttributes(); });
    }

    /* Fire and forget: the studio never waits on the cart and never stops for it. The order
       button writes the cart again, so a write that fails here costs nothing. */
    ensureCart() {
      var self = this;
      return this.queueCart(function () { return self.syncCart(); })
        .catch(function () { /* the order button tries again */ });
    }

    /* The studio covers the screen, so while it is open the cart cannot be reached and one a
       size behind hurts nobody. On the way out it has to be right: the header icon and /cart
       are live again. A cart with no figurine in it is left alone — closing the studio must
       never be the thing that puts one there. */
    reconcileCart() {
      var self = this;
      if (!this.state.token) return;
      this.queueCart(function () {
        return self.cartGet('cart.js').then(function (cart) {
          if (!self.studioLines(cart).length) return null;
          return self.syncCart();
        });
      }).catch(function () { /* nothing more to do out here */ });
    }

    /* A figurine another draft left behind, gone before this one starts. Clearing is a
       removal, never an addition, so nothing is counted for it. */
    dropOtherCart() {
      var self = this;
      var token = this.state.token;
      this.queueCart(function () {
        return self.cartGet('cart.js').then(function (cart) {
          var lines = self.studioLines(cart);
          if (!lines.length) return null;
          if (token && lines.every(function (line) { return line.properties._studio === token; })) return null;
          return self.cartPost('cart/clear.js');
        });
      }).catch(function () { /* the order button clears it anyway */ });
    }

    /* Nothing of this draft stays in the cart: a removal, so nothing is counted. */
    clearCart() {
      var self = this;
      this.queueCart(function () {
        return self.cartGet('cart.js').then(function (cart) {
          if (!self.studioLines(cart).length) return null;
          return self.cartPost('cart/clear.js');
        });
      }).catch(function () { /* an empty draft cannot be ordered anyway */ });
    }

    /* The e-mail typed in the studio, put on the cart as its buyer, so checkout opens with it
       filled in. The Storefront API is the only way in: checkout drops a checkout[email] in
       the URL on its own redirect. Never in the way of the order: it resolves whatever
       happens, and gives up after a few seconds. */
    prefillEmail(cart) {
      var email = this.state.email;
      if (!email || !cart || !cart.token || typeof fetch !== 'function') return Promise.resolve();
      var options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          /* In the studio's language, or the cart falls back to English and so does checkout. */
          query: 'mutation($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!, $language: LanguageCode) @inContext(language: $language) { cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) { userErrors { message } } }',
          variables: {
            cartId: 'gid://shopify/Cart/' + cart.token, buyerIdentity: { email: email },
            language: ((window.Shopify && Shopify.locale) || 'en').split('-')[0].toUpperCase()
          }
        })
      };
      var timer = null;
      if (typeof AbortController === 'function') {
        var abort = new AbortController();
        options.signal = abort.signal;
        timer = setTimeout(function () { abort.abort(); }, 3000);
      }
      return fetch('/api/2026-07/graphql.json', options)
        .catch(function () { /* checkout asks for it again */ })
        .then(function () { if (timer) clearTimeout(timer); });
    }

    addToCart() {
      var self = this;
      /* Every tap, before anything can go wrong: next to checkout_click (sent only once the
         cart is written) it tells "nobody tapped" apart from "the tap failed". */
      beacon('order_tap', { token: this.state.token }, true);
      var problem = this.validate();
      if (problem) {
        beacon('checkout_error', { token: this.state.token, props: { stage: 'validate' } }, true);
        this.showError(problem);
        return;
      }
      if (!this.intendedItems()) {
        beacon('checkout_error', { token: this.state.token, props: { stage: 'items' } }, true);
        this.showError(this.dataset.errCart || 'Something went wrong. Please try again.');
        return;
      }
      this.showError('');
      var btn = this.querySelector('[data-mq-add]');
      if (btn) btn.disabled = true;
      var total = this.orderCents(this.sizes[this.state.size]);
      var split = this.payDeposit() ? this.split() : null;
      /* What checkout should ask for: the deposit, or the whole price. */
      var due = split ? split.now : total;
      var currency = this.dataset.currency || 'EUR';
      var count = this.numPets;
      var stage = 'save';

      this.save().then(function () {
        stage = 'cart';
        /* Usually the cart is already right, from the size step: then nothing is written
           and nothing is counted a second time. */
        return self.queueCart(function () { return self.syncCart(); });
      }).then(function (cart) {
        stage = 'prefill';
        /* What Shopify charges, next to what the studio showed: a difference means the
           automatic discount no longer matches, and shows up in Insights. */
        var cartTotal = cart && typeof cart.total_price === 'number' ? cart.total_price : null;
        track('OrderClick', { value: total / 100, currency: currency, num_items: count });
        beacon('checkout_click', { token: self.state.token, props: {
          size: self.state.size, value: total / 100, num_pets: count,
          cart_value: cartTotal === null ? null : cartTotal / 100, price_match: cartTotal === null ? null : cartTotal === due,
          deposit: split ? split.now / 100 : null
        } }, true);
        return self.prefillEmail(cart);
      }).then(function () {
        /* Under the language prefix (/nl/checkout), so checkout opens in the studio's language. */
        window.location.href = self.cartRoot() + 'checkout';
      }).catch(function (err) {
        beacon('checkout_error', { token: self.state.token, props: {
          stage: stage, message: String((err && err.message) || err || '').slice(0, 64)
        } }, true);
        if (btn) btn.disabled = false;
        self.showError(self.dataset.errCart || 'Something went wrong. Please try again.');
      });
    }
  }

  if (!customElements.get('mq-studio')) customElements.define('mq-studio', MqStudio);

  /* Back from checkout: browsers restore the page as it was left, with the order button
     still disabled mid-redirect. Make it usable again. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('mq-studio [data-mq-add]').forEach(function (btn) { btn.disabled = false; });
  });

  /* ---------------- wiring ---------------- */

  function studio() {
    return document.querySelector('mq-studio');
  }

  /* Which button opened the studio, by where it sits: the Retargeting page's "Opened from". */
  var OPENERS = [['[data-mq-sticky]', 'sticky'], ['header-drawer', 'menu'], ['header-component, header', 'header'],
    ['.ps-hero', 'hero'], ['.mq-steps', 'steps'], ['.mq-proof-section', 'proof'], ['.mq-pricing', 'pricing'],
    ['.mq-promise', 'promise'], ['.ps-reasons', 'reasons'], ['.ps-social', 'social'], ['.ps-closing', 'closing'],
    ['.mq-closing', 'closing']];
  function openerOf(el) {
    try {
      for (var i = 0; i < OPENERS.length; i++) if (el.closest(OPENERS[i][0])) return OPENERS[i][1];
      var section = el.closest('.shopify-section');
      if (section && section.id) {
        return section.id.replace(/^shopify-section-(template--\d+__)?/, '').replace(/_[A-Za-z0-9]{6}$/, '').slice(0, 40);
      }
    } catch (e) { /* an old browser: no name */ }
    return 'page';
  }

  /* Studio buttons say "Continue with {name}" while a draft with photos is waiting.
     Only the button's own text node changes, so arrows and icons stay. */
  function labelButtons(s) {
    var text = '';
    if (s && s.resumable) {
      text = s.displayName()
        ? (s.dataset.txtResumeNamed || '').replace('{name}', s.displayName())
        : (s.dataset.txtResume || '');
    }
    document.querySelectorAll('[data-mq-open]').forEach(function (btn) {
      if (btn.dataset.mqPreselect) return; /* size buttons keep "Choose this size" */
      var node = Array.from(btn.childNodes).find(function (n) { return n.nodeType === 3 && n.textContent.trim(); });
      if (!node) return;
      if (btn.dataset.mqLabel === undefined) btn.dataset.mqLabel = node.textContent;
      node.textContent = text ? ' ' + text + ' ' : btn.dataset.mqLabel;
    });
  }

  function bindUploaders() {
    document.querySelectorAll('[data-mq-upload]').forEach(function (input) {
      if (input.dataset.mqBound) return;
      input.dataset.mqBound = '1';

      input.addEventListener('change', function () {
        var files = Array.from(input.files || []);
        var s = studio();
        if (s) {
          s.open(files, undefined, openerOf(input));
          var pre = input.dataset.mqPreselect;
          if (pre) {
            var radio = s.querySelector('[name="mq-size"][value="' + pre + '"]');
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
        input.value = '';
      });

      var zone = input.closest('.mq-drop');
      if (!zone) return;
      ['dragenter', 'dragover'].forEach(function (ev) {
        zone.addEventListener(ev, function (e) { e.preventDefault(); zone.classList.add('is-over'); });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        zone.addEventListener(ev, function (e) { e.preventDefault(); zone.classList.remove('is-over'); });
      });
      zone.addEventListener('drop', function (e) {
        var files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
        var s = studio();
        if (files.length && s) s.open(files, undefined, openerOf(zone));
      });
    });

    /* Buttons that open the studio without a file (the keepsake page's "Begin met je huisdier"). */
    document.querySelectorAll('[data-mq-open]').forEach(function (btn) {
      if (btn.dataset.mqBound) return;
      btn.dataset.mqBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var s = studio();
        if (!s) return;
        if (btn.dataset.mqContext) s.dataset.context = btn.dataset.mqContext;
        s.open([], btn.dataset.mqPreselect || 'cta', openerOf(btn));
        var pre = btn.dataset.mqPreselect;
        if (pre) {
          var radio = s.querySelector('[name="mq-size"][value="' + pre + '"]');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  function bindSticky() {
    var bar = document.querySelector('[data-mq-sticky]');
    if (!bar || bar.dataset.bound) return;
    bar.dataset.bound = '1';
    document.body.classList.add('mq-has-sticky');
    var suppressed = 0;
    var onScroll = function () {
      bar.classList.toggle('is-visible', suppressed === 0 && window.scrollY > window.innerHeight * 0.7);
    };
    /* Sections that carry their own button switch the bar off while in view. */
    var offs = Array.from(document.querySelectorAll('[data-mq-sticky-off]'));
    if (offs.length && 'IntersectionObserver' in window) {
      var seen = new Set();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) seen.add(e.target); else seen.delete(e.target); });
        suppressed = seen.size;
        onScroll();
      }, { threshold: 0.15 });
      offs.forEach(function (el) { io.observe(el); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Scroll-depth beacons for the landing page KPIs. One event per marker per view. */
  function bindDepth() {
    var markers = Array.from(document.querySelectorAll('[data-mq-depth]'));
    if (!markers.length || !('IntersectionObserver' in window)) return;
    var seen = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var key = entry.target.dataset.mqDepth;
        if (entry.isIntersecting && !seen[key]) {
          seen[key] = true;
          track('ScrollDepth', { block: key });
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    markers.forEach(function (m) { io.observe(m); });
  }

  var pageViewed = false;

  function boot() {
    utm();
    /* A page view counts people over time (a stored id), so only with analytics consent;
       the visit snippet counts every arrival without one. */
    if (!pageViewed && analyticsAllowed()) { pageViewed = true; beacon('page_view'); }
    bindUploaders();
    labelButtons(studio());
    bindSticky();
    bindDepth();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
